import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Grid,
  Card,
  CircularProgress,
  IconButton,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Chip,
  Stack,
  Alert
} from '@mui/material';
import { getAllProdottiByNow } from '../APIs/ProductsAPI';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../contexts/SnackBarContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { getAllSottocategorie } from '../APIs/SubCategoriesAPI';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon from '@mui/icons-material/RemoveRounded';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { ReactComponent as DefaultPlaceHolder } from '../images/DefaultPlaceHolder.svg';
import { useTheme } from '@mui/material';
import Autocomplete from '../components/SearchableAutocomplete';
import { getAllCategorie, getMyCategoryOrder, getMyCategoryVisibility, saveMyCategoryOrder } from '../APIs/CategoriesAPI';
import { aggiornaTavolo, claimTableSession, getAllTavoli, releaseTableSession } from '../APIs/TablesAPI';
import {
  aggiornaComanda,
  creaComanda,
  getAllOrdini,
  getKitchenPrintState,
  saveKitchenPrintState,
} from '../APIs/OrdersAPI';
import { printDocument } from '../APIs/PrintersAPI';
import { getAllPrenotazioni } from '../APIs/ReservationsAPI';
import { getAllPackagesByNow } from '../APIs/PackagesAPI';
import Close from '@mui/icons-material/Close';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import { SetupWSEventListener } from '../utils/setupWSEventListener';
import DettaglioComande from '../components/DettaglioComande';
import ModalVariant from '../components/ModalVariant';
import { getTenantTranslation } from '../utils/getTenantTranslation';
import { ModalNotes } from '../components/modalNotes';
import { useNavigate } from 'react-router-dom';
import { getTenantSettings } from '../APIs/TenantSettingsAPI';
import { getAllFixedPriceMenus } from '../APIs/FixedPriceMenusAPI';
import { formatBoardBasis, getGuestBoardBasis, isIncludedByBoardBasis } from '../utils/boardBasis';
import { appendPrintNote } from '../utils/printNotes';
import { formatCommandItem, formatCourseHeading, formatCourseSeparator } from '../utils/printOrderLayout';
import { resolveOrderCoverQuantity } from '../utils/orderCovers';
import { calculateDraftProductPricing } from '../utils/productLinePricing';
import { applyUserCategoryVisibility } from '../utils/categoryVisibility';
import {
  buildKitchenDiff,
  buildKitchenTargets,
  resolveProductPrinterTargets,
  makeKitchenSnapshot,
  normalizeKitchenLines,
} from '../utils/kitchenWorkflow';

const formatDateTime24 = (value) =>
  new Date(value).toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });

const normalizePrintCustomer = (value) => {
  const label = String(value || '').trim();
  return !label || /^nessun cliente/i.test(label) || /^[-–—]+$/.test(label) ? '' : label;
};

const resolvePackagePrice = (pkg, prodotti) => {
  if (!pkg) return 0;
  if (pkg.price != null && pkg.price !== undefined) return Number(pkg.price) || 0;
  if (pkg.manualPrice != null && pkg.manualPrice !== undefined) return Number(pkg.manualPrice) || 0;
  if (!Array.isArray(pkg.items)) return Number(pkg.price) || 0;
  return pkg.items.reduce((acc, item) => {
    const product = prodotti.find(p => String(p.id) === String(item.productId));
    const qty = Math.max(1, parseInt(item.quantity || 1, 10) || 1);
    return acc + (Number(product?.price) || 0) * qty;
  }, 0);
};

const isTastingMenu = (pkg) => pkg?.type === 'TASTING_MENU';

const getOrderedPackageItems = (pkg) => (
  Array.isArray(pkg?.items)
    ? [...pkg.items].sort((a, b) => {
      const courseDiff = Number(a?.courseIndex || 1) - Number(b?.courseIndex || 1);
      if (courseDiff !== 0) return courseDiff;
      return Number(a?.sortOrder ?? 0) - Number(b?.sortOrder ?? 0);
    })
    : []
);

const getOrderPackageItems = (orderPackage) => {
  if (isTastingMenu(orderPackage?.package) && Array.isArray(orderPackage?.items) && orderPackage.items.length > 0) {
    return getOrderedPackageItems({ items: orderPackage.items });
  }
  return getOrderedPackageItems(orderPackage?.package);
};

const getCourseData = (item, fallbackIndex = 1) => ({
  index: Math.max(1, Math.trunc(Number(item?.courseIndex || fallbackIndex) || fallbackIndex)),
  name: String(item?.courseName || '').trim(),
});

const compareOrdersByCourse = (a, b) => {
  const aTime = new Date(a?.createdAt).getTime();
  const bTime = new Date(b?.createdAt).getTime();
  if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) return aTime - bTime;
  if (Number.isFinite(aTime) !== Number.isFinite(bTime)) return Number.isFinite(aTime) ? -1 : 1;
  return Number(a?.id || 0) - Number(b?.id || 0);
};

const applyCategoryOrder = (categories, categoryIds) => {
  const positions = new Map((categoryIds || []).map((id, index) => [Number(id), index]));
  return [...(categories || [])].sort((a, b) => {
    const aPosition = positions.get(Number(a.id));
    const bPosition = positions.get(Number(b.id));
    if (aPosition != null && bPosition != null) return aPosition - bPosition;
    if (aPosition != null) return -1;
    if (bPosition != null) return 1;
    return 0;
  });
};

const getPackageItemName = (item, prodotti, fallback) => {
  const product = item?.product || prodotti.find((p) => Number(p.id) === Number(item?.productId));
  return item?.productName || product?.name || fallback;
};

const getPackageItemVariantName = (item) => item?.variantName || item?.variant?.name || '';

const NON_RESERVATION_ORDER_KEY_PREFIX = "nonReservationOrder";
const NON_RESERVATION_TABLE_KEY_PREFIX = "nonReservationTable";
const getNonReservationOrderStorageKey = (tenant) => `${NON_RESERVATION_ORDER_KEY_PREFIX}:${tenant || "default"}`;
const getNonReservationTableStorageKey = (tenant) => `${NON_RESERVATION_TABLE_KEY_PREFIX}:${tenant || "default"}`;

const getNonReservationTableMarkers = (tenant) => {
  try {
    const raw = localStorage.getItem(getNonReservationTableStorageKey(tenant));
    const markers = JSON.parse(raw || "[]");
    return new Set((Array.isArray(markers) ? markers : []).map(String));
  } catch {
    return new Set();
  }
};

const updateNonReservationOrderMarker = (tenant, orderId, shouldMark) => {
  const normalizedOrderId = Number(orderId);
  if (!Number.isInteger(normalizedOrderId) || normalizedOrderId <= 0) return;

  try {
    const key = getNonReservationOrderStorageKey(tenant);
    const existing = JSON.parse(localStorage.getItem(key) || "[]");
    const ids = new Set((Array.isArray(existing) ? existing : []).map(Number).filter((id) => Number.isInteger(id) && id > 0));
    if (shouldMark) ids.add(normalizedOrderId);
    else ids.delete(normalizedOrderId);
    localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // Ignore local marker errors; the backend order update still carries the source of truth.
  }
};

const updateNonReservationTableMarker = (tenant, tableId, reservationId, shouldMark) => {
  const normalizedTableId = Number(tableId);
  const normalizedReservationId = Number(reservationId);
  if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return;
  if (!Number.isInteger(normalizedReservationId) || normalizedReservationId <= 0) return;

  try {
    const key = getNonReservationTableStorageKey(tenant);
    const marker = `${normalizedTableId}:${normalizedReservationId}`;
    const existing = JSON.parse(localStorage.getItem(key) || "[]");
    const markers = new Set(Array.isArray(existing) ? existing.map(String) : []);
    if (shouldMark) markers.add(marker);
    else markers.delete(marker);
    localStorage.setItem(key, JSON.stringify([...markers]));
  } catch {
    // Ignore local marker errors; the backend order update still carries the source of truth.
  }
};

const getPositiveInt = (...values) => {
  for (const value of values) {
    const normalized = Number(value);
    if (Number.isInteger(normalized) && normalized > 0) return normalized;
  }
  return 0;
};

const buildTableGuestAllocations = (primaryTable, selectedTables = [], totalGuests = 1) => {
  if (!primaryTable?.id) return [];

  const primaryId = Number(primaryTable.id);
  const tablesById = new Map();
  tablesById.set(primaryId, primaryTable);
  (Array.isArray(selectedTables) ? selectedTables : []).forEach((table) => {
    const tableId = Number(table?.id);
    if (Number.isInteger(tableId) && tableId > 0) tablesById.set(tableId, table);
  });

  const tables = [...tablesById.values()];
  const total = getPositiveInt(totalGuests, 1);
  let remaining = total;

  return tables.map((table, index) => {
    const tableId = Number(table.id);
    const capacity = getPositiveInt(table.maxSeats, table.seats, table.capacity);
    const isLast = index === tables.length - 1;
    const guestCount = isLast
      ? remaining
      : (capacity > 0 ? Math.min(remaining, capacity) : remaining);
    remaining = Math.max(0, remaining - Math.max(0, guestCount));

    return {
      tableId,
      guestCount: Math.max(0, guestCount),
    };
  }).filter((entry) => Number.isInteger(entry.tableId) && entry.tableId > 0);
};

const orderBelongsToTable = (order, tableId) => {
  const normalizedTableId = Number(tableId);
  if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return false;
  if (Number(order?.tableId ?? order?.table?.id) === normalizedTableId) return true;
  return (Array.isArray(order?.orderTables) ? order.orderTables : []).some((link) => (
    Number(link?.tableId ?? link?.table?.id) === normalizedTableId
  ));
};

function SelezioneComanda({ open, tavolo, onClose, onTableOccupied, onOrderSent }) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const tenantType = localStorage.getItem('tenantType');
  const isRestaurantTenant = String(tenantType || '').toUpperCase() === 'RISTORANTE';
  const [prodotti, setProdotti] = useState([]);
  const [orders, setOrders] = useState([]);
  const [openVariant, setOpenVariant] = useState(false);
  const [variantData, setVariantData] = useState([]);
  const [prodottoSelezionato, setProdottoSelezionato] = useState(null);
  const [tavoli, setTavoli] = useState([]);
  const [quantita, setQuantita] = useState({});
  const [activeVariantByProduct, setActiveVariantByProduct] = useState({});
  const [quantitaPeso, setQuantitaPeso] = useState({});
  const [weightVariantRows, setWeightVariantRows] = useState({});
  const [categorie, setCategorie] = useState([]);
  const [openCategoryOrder, setOpenCategoryOrder] = useState(false);
  const [categoryOrderDraft, setCategoryOrderDraft] = useState([]);
  const [savingCategoryOrder, setSavingCategoryOrder] = useState(false);
  const [sottoCategorie, setSottocategorie] = useState([]);
  const [cercaPiatto, setCercaPiatto] = useState('');
  const [selectedMainCategory, setSelectedMainCategory] = useState('ALL');
  const [selectedCategoria, setSelectedCategoria] = useState('ALL');
  const [selectedTavolo, setSelectedTavolo] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [showMobileOrder, setShowMobileOrder] = useState(false);
  const [cart, setCart] = useState([]);
  const [activeReservation, setActiveReservation] = useState(null);
  const [showPic, setShowPic] = useState(true);
  const [showFixedMenuNameOnKitchenPrint, setShowFixedMenuNameOnKitchenPrint] = useState(false);
  const [orderPrintFontSize, setOrderPrintFontSize] = useState('NORMAL');
  const [numeroCoperti, setNumeroCoperti] = useState(0);
  const [confirmReservationSeats, setConfirmReservationSeats] = useState(false);
  const [isNonReservationClientSelection, setIsNonReservationClientSelection] = useState(false);
  const [addTableFlag, setAddTableFlag] = useState(false);
  const [openModalConfirmSeat, setOpenModalConfirmSeat] = useState(true);
  const [note, setNote] = useState({});
  const [openNote, setOpenNote] = useState(false);
  const [prodottoNote, setProdottoNote] = useState(null);
  const [idTables, setIdTables] = useState(() => (tavolo ? [tavolo] : []));
  const theme = useTheme();
  const navigate = useNavigate();
  const contextTP = theme.palette;
  const { showSnackbar } = useSnackbar();
  const [availablePackages, setAvailablePackages] = useState([]);
  const [packageQuantita, setPackageQuantita] = useState({});
  const [packageImageIndex, setPackageImageIndex] = useState({});
  const [activeFixedPriceMenus, setActiveFixedPriceMenus] = useState([]);
  const [activeFixedPriceMenu, setActiveFixedPriceMenu] = useState(null);
  const [fixedMenuDrafts, setFixedMenuDrafts] = useState([]);
  const [openFixedMenu, setOpenFixedMenu] = useState(false);
  const [fixedMenuSelections, setFixedMenuSelections] = useState({});
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [hasLoadedCatalog, setHasLoadedCatalog] = useState(false);
  const [isSendingOrder, setIsSendingOrder] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [targetCourseId, setTargetCourseId] = useState('NEW');
  const autoAppliedReservationIdRef = useRef(null);
  const autoAddedPackageIdsRef = useRef([]);
  const tableWasOccupiedRef = useRef(false);
  const orderSentRef = useRef(false);
  const initialTableStatusRef = useRef(tavolo?.status || null);
  const confirmedCoversRef = useRef({ tableId: null, count: null });
  const onCloseRef = useRef(onClose);
  const claimedTableIdsRef = useRef(new Set());

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const getDefaultSeatsForContext = useCallback((reservation, table) => {
    return getPositiveInt(
      reservation?.guests,
      table?.takenSeats,
      table?.maxSeats,
      table?.seats,
      table?.capacity,
      1
    );
  }, []);

  useEffect(() => {
    const activeTable = tavolo || selectedTavolo;
    const activeTableId = Number(activeTable?.id);
    if (Number(confirmedCoversRef.current.tableId) !== activeTableId) {
      confirmedCoversRef.current = { tableId: activeTableId || null, count: null };
    }
    setIdTables((prev) => {
      if (!activeTable) return [];
      if (addTableFlag && Array.isArray(prev) && prev.some((tab) => Number(tab?.id) === Number(activeTable.id))) {
        return prev;
      }
      return [activeTable];
    });
  }, [addTableFlag, selectedTavolo, tavolo]);

  const getReservationRecipientLabel = useCallback((reservationItem) => {
    if (!reservationItem) return '';
    const clientName = [reservationItem.client?.name, reservationItem.client?.surname]
      .filter(Boolean)
      .join(' ')
      .trim();
    return clientName || reservationItem.company?.companyName || '';
  }, []);

  const activeReservationLabel = useMemo(
    () => getReservationRecipientLabel(activeReservation),
    [activeReservation, getReservationRecipientLabel]
  );

  const activeBoardBasis = useMemo(
    () => (confirmReservationSeats ? getGuestBoardBasis(activeReservation) : ''),
    [activeReservation, confirmReservationSeats]
  );

  const activeReservationDisplayLabel = useMemo(() => {
    if (activeReservation && (!confirmReservationSeats || isNonReservationClientSelection)) {
      return t("checkout_page.notBookedClient");
    }
    return [
      activeReservationLabel,
      activeBoardBasis ? `Board ${formatBoardBasis(activeBoardBasis)}` : null,
    ].filter(Boolean).join(' - ');
  }, [activeReservation, activeReservationLabel, activeBoardBasis, confirmReservationSeats, isNonReservationClientSelection, t]);
  const activeCoverTable = useMemo(
    () => tavolo || selectedTavolo || (Array.isArray(idTables) ? idTables[0] : null),
    [idTables, selectedTavolo, tavolo]
  );
  const activeCoverTableCapacity = useMemo(
    () => getPositiveInt(activeCoverTable?.maxSeats, activeCoverTable?.seats, activeCoverTable?.capacity),
    [activeCoverTable]
  );
  const isOverCapacityCoverCount = activeCoverTableCapacity > 0
    && getPositiveInt(numeroCoperti) > activeCoverTableCapacity;

  const activeReservationPackageIds = useMemo(() => {
    const idsFromReservationPackages = Array.isArray(activeReservation?.reservationPackages)
      ? activeReservation.reservationPackages
        .map((entry) => Number(entry?.packageId ?? entry?.package?.id))
        .filter((id) => Number.isInteger(id) && id > 0)
      : [];

    const idsFromPackageIds = Array.isArray(activeReservation?.packageIds)
      ? activeReservation.packageIds
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0)
      : [];

    return [...new Set([...idsFromReservationPackages, ...idsFromPackageIds])];
  }, [activeReservation]);

  const removeAutoAddedReservationPackages = useCallback(() => {
    const packageIdsToRemove = [
      ...new Set([
        ...(Array.isArray(autoAddedPackageIdsRef.current) ? autoAddedPackageIdsRef.current : []),
        ...activeReservationPackageIds,
      ]),
    ];
    if (packageIdsToRemove.length === 0) return;

    setPackageQuantita((prev) => {
      const next = { ...prev };
      packageIdsToRemove.forEach((id) => {
        if (Object.prototype.hasOwnProperty.call(next, id)) next[id] = 0;
      });
      return next;
    });
    autoAppliedReservationIdRef.current = null;
    autoAddedPackageIdsRef.current = [];
  }, [activeReservationPackageIds]);

  const productCategoryEntries = useCallback((product) => {
    if (Array.isArray(product?.categories) && product.categories.length > 0) {
      return product.categories;
    }
    if (product?.categoryId || product?.subcategoryId) {
      return [{
        categoryId: product?.categoryId ?? null,
        subcategoryId: product?.subcategoryId ?? null,
      }];
    }
    return [];
  }, []);

  const productHasSubcategory = useCallback((product, subcategoryId) => {
    const normalizedSubcategoryId = Number(subcategoryId);
    if (!Number.isInteger(normalizedSubcategoryId)) return false;

    return productCategoryEntries(product).some((entry) =>
      Number(entry?.subcategoryId ?? entry?.subcategory?.id) === normalizedSubcategoryId
    );
  }, [productCategoryEntries]);

  const productHasCategory = useCallback((product, categoryId) => {
    const normalizedCategoryId = Number(categoryId);
    if (!Number.isInteger(normalizedCategoryId)) return false;

    return productCategoryEntries(product).some((entry) =>
      Number(entry?.categoryId ?? entry?.category?.id) === normalizedCategoryId
    );
  }, [productCategoryEntries]);

  const getProductCategoryLabels = useCallback((product) => {
    const entries = productCategoryEntries(product);
    const labels = entries
      .map((entry) => {
        const categoryId = Number(entry?.categoryId ?? entry?.category?.id);
        const subcategoryId = Number(entry?.subcategoryId ?? entry?.subcategory?.id);
        const categoryName = entry?.category?.name || categorie.find((cat) => Number(cat.id) === categoryId)?.name;
        const subcategoryName = entry?.subcategory?.name || sottoCategorie.find((sub) => Number(sub.id) === subcategoryId)?.name;
        if (categoryName && subcategoryName) return `${categoryName} - ${subcategoryName}`;
        return categoryName || subcategoryName || null;
      })
      .filter(Boolean);

    return labels.length ? [...new Set(labels)].join(', ') : t('NoCategory');
  }, [categorie, productCategoryEntries, sottoCategorie, t]);

  const productCategoryNamesContain = useCallback((product, text) => {
    const needle = String(text || '').toLowerCase();
    if (!needle) return false;

    return productCategoryEntries(product).some((entry) => {
      const categoryId = Number(entry?.categoryId ?? entry?.category?.id);
      const subcategoryId = Number(entry?.subcategoryId ?? entry?.subcategory?.id);
      const categoryName = entry?.category?.name || categorie.find((cat) => Number(cat.id) === categoryId)?.name || '';
      const subcategoryName = entry?.subcategory?.name || sottoCategorie.find((sub) => Number(sub.id) === subcategoryId)?.name || '';
      return categoryName.toLowerCase().includes(needle) || subcategoryName.toLowerCase().includes(needle);
    });
  }, [categorie, productCategoryEntries, sottoCategorie]);

  const getCopertoProductForTable = useCallback((table) => {
    if (String(tenantType || '').toUpperCase() !== 'RISTORANTE') return null;

    const coverProducts = (Array.isArray(prodotti) ? prodotti : []).filter((product) => {
      const name = String(product?.name || '').trim().toLowerCase();
      return name.includes('copert') || productCategoryNamesContain(product, 'copert');
    });
    if (coverProducts.length === 0) return null;

    const tableRoomId = Number(table?.roomId);
    const hasTableRoom = Number.isInteger(tableRoomId) && tableRoomId > 0;
    const getProductRoomIds = (product) => (
      Array.isArray(product?.roomIds)
        ? product.roomIds.map(Number).filter((id) => Number.isInteger(id) && id > 0)
        : []
    );
    const hasAssignedRoom = (product) => getProductRoomIds(product).length > 0;

    if (hasTableRoom) {
      const roomCover = coverProducts.find(
        (product) => getProductRoomIds(product).includes(tableRoomId)
      );
      if (roomCover) return roomCover;
    }

    const defaultCover = coverProducts.find((product) => !hasAssignedRoom(product));
    if (defaultCover) return defaultCover;

    // Legacy fallback while existing tenants still have no room assignments.
    return coverProducts.some(hasAssignedRoom) ? null : coverProducts[0];
  }, [prodotti, productCategoryNamesContain, tenantType]);

  const buildComboKey = useCallback((productId, variantIds = []) => {
    const normalized = [...variantIds]
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .sort((a, b) => a - b);
    return `${Number(productId)}:${normalized.length ? normalized.join('-') : 'base'}`;
  }, []);

  const getSelectedVariantsForProduct = useCallback((productId) => {
    return variantData.filter((v) => Number(v?.productId ?? v?.product?.id) === Number(productId));
  }, [variantData]);

  const getActiveVariantForProduct = useCallback((productId) => {
    const selected = getSelectedVariantsForProduct(productId);
    if (!selected.length) return null;
    const activeId = Number(activeVariantByProduct[productId]);
    return selected.find((v) => Number(v.id) === activeId) || selected[selected.length - 1];
  }, [activeVariantByProduct, getSelectedVariantsForProduct]);

  const getComboVariantIds = useCallback((productId) => {
    const selected = getSelectedVariantsForProduct(productId);
    return selected
      .map((v) => Number(v.id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .sort((a, b) => a - b);
  }, [getSelectedVariantsForProduct]);

  const normalizeVariantIds = useCallback((variantIds = []) => {
    return (Array.isArray(variantIds) ? variantIds : [])
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .sort((a, b) => a - b);
  }, []);

  const sameVariantIds = useCallback((a = [], b = []) => {
    const normalizedA = normalizeVariantIds(a);
    const normalizedB = normalizeVariantIds(b);
    return normalizedA.length === normalizedB.length
      && normalizedA.every((id, index) => id === normalizedB[index]);
  }, [normalizeVariantIds]);

  const getVariantSnapshotsForProduct = useCallback((product, variantIds = []) => {
    const normalizedIds = normalizeVariantIds(variantIds);
    return normalizedIds
      .map((variantId) => (Array.isArray(product?.variants)
        ? product.variants.find((variant) => Number(variant.id) === variantId)
        : null))
      .filter(Boolean);
  }, [normalizeVariantIds]);

  const getDraftProductPricing = useCallback((product, variantsExtra = 0, quantity = 1) => {
    return calculateDraftProductPricing({
      baseUnitPrice: product?.price,
      variantExtra: variantsExtra,
      quantity,
      pricePerWeight: product?.pricePerWeight,
      included: isIncludedByBoardBasis(product, activeBoardBasis),
    });
  }, [activeBoardBasis]);

  const getDraftProductUnitPrice = useCallback((product, variantsExtra = 0) => (
    getDraftProductPricing(product, variantsExtra, 1).displayUnitPrice
  ), [getDraftProductPricing]);

  const getProductTotalQty = useCallback((productId) => {
    const prefix = `${Number(productId)}:`;
    return Object.entries(quantita).reduce((sum, [key, qty]) => {
      if (!key.startsWith(prefix)) return sum;
      return sum + Number(qty || 0);
    }, 0);
  }, [quantita]);

  const totale = Object.values(quantita);
  const selectedProdottiNormali = totale.reduce((acc, q) => acc + q, 0);
  const selectedProdottiPeso = Object.values(quantitaPeso).reduce((acc, rows) => {
    const normalized = Array.isArray(rows) ? rows : [];
    return acc + normalized.filter((q) => Number(q) > 0).length;
  }, 0);
  const selectedPackages = Object.entries(packageQuantita)
    .map(([packageId, quantity]) => ({ packageId: Number(packageId), quantity: Number(quantity) || 0 }))
    .filter(item => item.quantity > 0)
    .map(item => {
      const pkg = availablePackages.find(p => String(p.id) === String(item.packageId));
      return { ...item, unitPrice: resolvePackagePrice(pkg, prodotti), note: '' };
    });

  const selectedPackagesCount = selectedPackages.reduce((acc, item) => acc + item.quantity, 0);
  const selectedProdotti = selectedProdottiNormali
    + selectedProdottiPeso
    + selectedPackagesCount
    + fixedMenuDrafts.length;

  const totaleCostiProdotti = prodotti.reduce((acc, prodotto) => {
    const weightTotal = prodotto.pricePerWeight
      ? (Array.isArray(quantitaPeso[prodotto.id]) ? quantitaPeso[prodotto.id].reduce((sum, q, index) => {
        const variantIds = normalizeVariantIds(weightVariantRows[prodotto.id]?.[index]);
        const variantsExtra = getVariantSnapshotsForProduct(prodotto, variantIds)
          .reduce((extra, variant) => extra + Number(variant?.price || 0), 0);
        return sum + getDraftProductPricing(prodotto, variantsExtra, Number(q || 0)).lineTotal;
      }, 0) : 0)
      : 0;
    const comboPrefix = `${Number(prodotto.id)}:`;

    const comboTotal = Object.entries(quantita).reduce((sum, [key, qty]) => {
      if (!key.startsWith(comboPrefix)) return sum;
      const variantPart = key.split(':')[1] || 'base';
      const variantIds = variantPart === 'base'
        ? []
        : variantPart.split('-').map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0);
      const variantsExtra = variantIds.reduce((extra, variantId) => {
        const variant = Array.isArray(prodotto.variants)
          ? prodotto.variants.find((v) => Number(v.id) === variantId)
          : null;
        return extra + Number(variant?.price || 0);
      }, 0);
      const unitPrice = getDraftProductUnitPrice(prodotto, variantsExtra);
      return sum + (unitPrice * Number(qty || 0));
    }, 0);

    return acc + weightTotal + comboTotal;
  }, 0);
  const totaleCostiPacchetti = selectedPackages.reduce((acc, pkg) => acc + pkg.unitPrice * pkg.quantity, 0);
  const totaleCostiMenuFisso = fixedMenuDrafts.reduce(
    (sum, draft) => sum + (Number(draft.finalPrice || 0) * Number(draft.quantity || 1)),
    0
  );
  const totaleCosti = totaleCostiProdotti + totaleCostiPacchetti + totaleCostiMenuFisso;

  const liveDraftItems = useMemo(() => {
    const productDraft = cart.map((item) => {
      const product = prodotti.find((p) => Number(p.id) === Number(item.productId));
      const quantity = Number(item.quantity || 0);
      if (!product || quantity <= 0) return null;

      const variantIds = Array.isArray(item.variantIds) ? item.variantIds : [];
      const selectedVariants = variantIds
        .map((id) => {
          const fromSnapshot = Array.isArray(item.variantSnapshots)
            ? item.variantSnapshots.find((v) => Number(v.id) === Number(id))
            : null;
          const fromProduct = Array.isArray(product.variants)
            ? product.variants.find((v) => Number(v.id) === Number(id))
            : null;
          return fromSnapshot || fromProduct || null;
        })
        .filter(Boolean);
      const variantsExtra = selectedVariants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
      const pricing = getDraftProductPricing(product, variantsExtra, quantity);
      const unitPrice = pricing.displayUnitPrice;
      const isIncluded = unitPrice === 0 && isIncludedByBoardBasis(product, activeBoardBasis);
      const variantsLabel = selectedVariants.map((variant) => variant?.name).filter(Boolean).join(' + ');

      const unitLabel = product.pricePerWeight
        ? (product.typeOfWeight === 'HECTOGRAM' ? 'hg' : 'kg')
        : '';
      return {
        id: `prd-${item.productId}-${item.comboKey || (variantIds.length ? variantIds.join('-') : 'base')}`,
        kind: 'product',
        comboKey: item.comboKey,
        isWeight: !!item.isWeight,
        productId: item.productId,
        variantIds,
        hasVariants: Array.isArray(product.variants) && product.variants.length > 0,
        categoryLabel: getProductCategoryLabels(product),
        name: product.name,
        variantsLabel,
        quantity,
        quantityLabel: unitLabel ? `${quantity}${unitLabel}` : `${quantity}`,
        unitPrice,
        total: pricing.lineTotal,
        isIncluded,
        priceLabel: isIncluded ? t("boardBasis.included", { defaultValue: "Included" }) : '',
        detailsLabel: '',
        note: item.note || '',
      };
    }).filter(Boolean);

    const packageDraft = selectedPackages.map(item => {
      const pkg = availablePackages.find(p => Number(p.id) === Number(item.packageId));
      const quantity = Number(item.quantity) || 0;
      const unitPrice = Number(item.unitPrice) || 0;
      if (!pkg || quantity === 0) return null;
      const detailsLabel = getOrderedPackageItems(pkg)
        .map(it => {
          const product = prodotti.find(p => Number(p.id) === Number(it.productId));
          const qty = Math.max(1, parseInt(it.quantity || 1, 10) || 1);
          const course = getCourseData(it);
          const coursePrefix = isTastingMenu(pkg)
            ? `${course.index}${course.name ? ` ${course.name}` : ''}: `
            : '';
          return product?.name ? `${coursePrefix}${product.name} x${qty}` : null;
        })
        .filter(Boolean)
        .join(', ');
      return {
        id: `pkg-${item.packageId}`,
        kind: 'package',
        packageId: Number(item.packageId),
        categoryLabel: isTastingMenu(pkg) ? t('orderPackages.tastingMenu') : t('orderPackages.label'),
        name: pkg.name || `${t('orderPackages.label')} ${item.packageId}`,
        quantity,
        quantityLabel: `${quantity}`,
        unitPrice,
        total: quantity * unitPrice,
        detailsLabel,
        packageType: pkg.type || 'STANDARD',
        packageItems: getOrderedPackageItems(pkg).map((pkgItem, pkgItemIndex) => {
          const product = pkgItem?.product || prodotti.find(p => Number(p.id) === Number(pkgItem.productId));
          return {
            productId: Number(pkgItem.productId),
            variantId: pkgItem.variantId ?? null,
            quantity: Number(pkgItem.quantity) || 1,
            courseIndex: Number(pkgItem.courseIndex) || 1,
            courseName: pkgItem.courseName || '',
            sortOrder: Number(pkgItem.sortOrder ?? pkgItemIndex),
            productName: product?.name || pkgItem.productName || t('newproduct'),
            variantName: getPackageItemVariantName(pkgItem),
          };
        }),
      };
    }).filter(Boolean);

    const fixedMenuItems = fixedMenuDrafts.flatMap((draft, draftIndex) => (
      (draft.selections || []).map((selection, selectionIndex) => {
        const selectionQuantity = Number(selection.quantity || 1) * Number(draft.quantity || 1);
        return {
          id: `${draft.clientId || `fixed-menu-${draftIndex}`}-${selectionIndex}`,
          kind: 'fixed-price-menu-selection',
          fixedMenuClientId: draft.clientId,
          categoryLabel: draft.menuName,
          name: selection.productName,
          quantity: selectionQuantity,
          quantityLabel: `${selectionQuantity}`,
          unitPrice: selectionIndex === 0 ? Number(draft.finalPrice || 0) : 0,
          total: selectionIndex === 0
            ? Number(draft.finalPrice || 0) * Number(draft.quantity || 1)
            : 0,
          courseIndex: Number(selection.courseIndex) || 1,
          courseName: selection.groupName || '',
          detailsLabel: Number(selection.extraPrice || 0) > 0
            ? t("fixedPriceMenu.supplementLabel", { price: Number(selection.extraPrice).toFixed(2) })
            : '',
        };
      })
    ));

    return [...productDraft, ...packageDraft, ...fixedMenuItems];
  }, [activeBoardBasis, cart, prodotti, selectedPackages, availablePackages, fixedMenuDrafts, getDraftProductPricing, getProductCategoryLabels, t]);

  const tableName = tavolo?.name || selectedTavolo?.name;
  const tableNote = tavolo?.note || selectedTavolo?.note || '';
  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');
  const selectedTableId = Number(tavolo?.id ?? selectedTavolo?.id);

  const claimAndTrackTableSession = useCallback(async (tableId) => {
    const normalizedTableId = Number(tableId);
    if (!Number.isInteger(normalizedTableId) || normalizedTableId <= 0) return null;
    const session = await claimTableSession(normalizedTableId, token, tenant);
    claimedTableIdsRef.current.add(normalizedTableId);
    return session;
  }, [token, tenant]);

  const syncShowImagesFromBackend = useCallback(async (silent = false) => {
    if (!token || !tenant) return;
    try {
      const settings = await getTenantSettings(token, tenant);
      setShowPic(settings?.showProductImages !== false);
      setShowFixedMenuNameOnKitchenPrint(settings?.showFixedMenuNameOnKitchenPrint === true);
      setOrderPrintFontSize(settings?.orderPrintFontSize || 'NORMAL');
    } catch (err) {
      if (!silent) {
        showSnackbar(err?.message || 'Errore nel recupero impostazioni sistema', 'error');
      }
    }
  }, [token, tenant, showSnackbar]);

  const activeCourseOrders = useMemo(() => {
    if (!Number.isInteger(selectedTableId) || selectedTableId <= 0) return [];
    const today = new Date();
    return (Array.isArray(orders) ? orders : [])
      .filter((order) => {
        if (!orderBelongsToTable(order, selectedTableId)) return false;
        if (order?.status === 'PAID' || order?.status === 'CANCELLED') return false;
        const createdAt = new Date(order?.createdAt);
        if (Number.isNaN(createdAt.getTime())) return false;
        return createdAt.getFullYear() === today.getFullYear()
          && createdAt.getMonth() === today.getMonth()
          && createdAt.getDate() === today.getDate();
      })
      .sort(compareOrdersByCourse);
  }, [orders, selectedTableId]);

  useEffect(() => {
    if (targetCourseId === 'NEW') return;
    const exists = activeCourseOrders.some((order) => Number(order.id) === Number(targetCourseId));
    if (!exists) setTargetCourseId('NEW');
  }, [activeCourseOrders, targetCourseId]);

  const selectedTargetCourseIndex = activeCourseOrders.findIndex(
    (order) => Number(order.id) === Number(targetCourseId)
  );
  const selectedTargetCourseLabel = selectedTargetCourseIndex >= 0
    ? `${selectedTargetCourseIndex + 1} - ${t('miscUi.selectedCourse')}`
    : t('miscUi.newCourse');
  const shouldRequestCovers = activeCourseOrders.length === 0
    && (
      Boolean(activeReservation)
      || !tavolo?.takenSeats
      || Number(tavolo.takenSeats) === 0
    );

  const resolveActiveTableContext = useCallback(() => {
    const tableId = Number(tavolo?.id ?? selectedTavolo?.id);
    if (!Number.isInteger(tableId) || tableId <= 0) return null;

    return tavolo
      || selectedTavolo
      || (Array.isArray(tavoli) ? tavoli.find((table) => Number(table?.id) === tableId) : null)
      || null;
  }, [selectedTavolo, tavoli, tavolo]);

  const productMatchesActiveTableRoom = useCallback((product) => {
    if (String(tenantType || '').toUpperCase() !== 'RISTORANTE') return true;

    const productRoomIds = Array.isArray(product?.roomIds)
      ? product.roomIds.map(Number).filter((id) => Number.isInteger(id) && id > 0)
      : [];
    if (productRoomIds.length === 0) return true;

    const activeTable = resolveActiveTableContext();
    if (!activeTable) return true;

    return productRoomIds.includes(Number(activeTable.roomId));
  }, [resolveActiveTableContext, tenantType]);

  const fixedMenuSelectedLines = useMemo(() => (
    (activeFixedPriceMenu?.groups || []).flatMap((group) => {
      const selectedIds = fixedMenuSelections[group.id] || [];
      return selectedIds.map((productId) => {
        const option = (group.options || []).find(
          (item) => Number(item.productId) === Number(productId)
        );
        const product = option?.product
          || prodotti.find((item) => Number(item.id) === Number(productId));
        return {
          groupId: Number(group.id),
          groupName: group.name,
          courseIndex: Number(group.courseIndex) || 1,
          productId: Number(productId),
          productName: product?.name || t("fixedPriceMenu.productFallback", { id: productId }),
          extraPrice: Number(option?.extraPrice || 0),
          quantity: 1,
        };
      });
    })
  ), [activeFixedPriceMenu, fixedMenuSelections, prodotti, t]);

  const fixedMenuSupplements = fixedMenuSelectedLines.reduce(
    (sum, selection) => sum + (Number(selection.extraPrice || 0) * Number(selection.quantity || 1)),
    0
  );
  const fixedMenuFinalPrice = Number(activeFixedPriceMenu?.basePrice || 0) + fixedMenuSupplements;

  const fixedMenuInvalidGroup = useMemo(() => {
    if (fixedMenuSelectedLines.length === 0) return { id: 'empty-selection' };
    return (activeFixedPriceMenu?.groups || []).find((group) => {
      const count = (fixedMenuSelections[group.id] || []).length;
      return count > Number(group.maxSelections || 1);
    });
  }, [activeFixedPriceMenu, fixedMenuSelectedLines, fixedMenuSelections]);

  const openFixedPriceMenuSelection = (menu) => {
    setActiveFixedPriceMenu(menu);
    setFixedMenuSelections({});
    setOpenFixedMenu(true);
  };

  const toggleFixedMenuOption = (group, productId) => {
    const normalizedId = Number(productId);
    const currentIds = fixedMenuSelections[group.id] || [];
    const selected = currentIds.includes(normalizedId);
    const max = Math.max(1, Number(group.maxSelections) || 1);

    let nextIds;
    if (selected) nextIds = currentIds.filter((id) => id !== normalizedId);
    else if (max === 1) nextIds = [normalizedId];
    else if (currentIds.length < max) nextIds = [...currentIds, normalizedId];
    else return;

    setFixedMenuSelections((current) => ({ ...current, [group.id]: nextIds }));
  };

  const addFixedPriceMenuDraft = () => {
    if (!activeFixedPriceMenu || fixedMenuInvalidGroup) return;
    setFixedMenuDrafts((current) => [
      ...current,
      {
        clientId: `fixed-menu-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        fixedPriceMenuId: Number(activeFixedPriceMenu.id),
        menuName: activeFixedPriceMenu.name,
        quantity: 1,
        basePrice: Number(activeFixedPriceMenu.basePrice || 0),
        supplementsTotal: fixedMenuSupplements,
        finalPrice: fixedMenuFinalPrice,
        selections: fixedMenuSelectedLines,
      },
    ]);
    setFixedMenuSelections({});
    setOpenFixedMenu(false);
  };

  const formatQuantityWithUnit = useCallback((quantity, product) => {
    const qty = Number(quantity || 0);
    if (!product?.pricePerWeight) return `${qty}`;
    const unit = product?.typeOfWeight === 'HECTOGRAM' ? 'hg' : 'kg';
    return `${qty}${unit}`;
  }, []);

  const getProductPrinterTargets = useCallback((product) => {
    return resolveProductPrinterTargets(
      product,
      tavolo?.roomId ?? tavolo?.room?.id ?? selectedTavolo?.roomId ?? selectedTavolo?.room?.id
    );
  }, [selectedTavolo?.roomId, selectedTavolo?.room?.id, tavolo?.roomId, tavolo?.room?.id]);

  const buildDifferentialPrintContent = useCallback((activeTable, title = 'TIPO STAMPA: DIFFERENZIALE', items = liveDraftItems) => {
    const nowLabel = formatDateTime24(new Date());
    const lines = [
      title,
      `TAVOLO: ${activeTable?.name || '-'}`,
      `DESTINAZIONE: ${selectedTargetCourseLabel}`,
      `DATA: ${nowLabel}`,
      '--------------------------------',
    ];

    (Array.isArray(items) ? items : []).forEach((item, index) => {
      const qtyLabel = item?.quantityLabel || `${Number(item?.quantity || 0)}`;
      const total = Number(item?.total || 0).toFixed(2);
      lines.push(`${index + 1}. ${item?.name || '-'} x${qtyLabel}  EUR ${total}`);
      if (item?.detailsLabel) lines.push(`   -> ${item.detailsLabel}`);
    });

    lines.push('--------------------------------');
    lines.push(`TOTALE DIFFERENZIALE: EUR ${Number(totaleCosti || 0).toFixed(2)}`);

    return lines.join('\r\n');
  }, [liveDraftItems, selectedTargetCourseLabel, totaleCosti]);

  const buildPrinterTargetsForDifferential = useCallback((activeTable) => {
    const linesByPrinter = new Map();
    const unassignedItems = [];
    const defaultCourseIndex = selectedTargetCourseIndex >= 0
      ? selectedTargetCourseIndex + 1
      : activeCourseOrders.length + 1;

    const addLineToPrinters = (printerNames, courseIndex, courseName, line) => {
      if (!Array.isArray(printerNames) || printerNames.length === 0) {
        unassignedItems.push(line);
        return;
      }
      printerNames.forEach((printerName) => {
        if (!linesByPrinter.has(printerName)) linesByPrinter.set(printerName, new Map());
        const printerCourses = linesByPrinter.get(printerName);
        if (!printerCourses.has(courseIndex)) {
          printerCourses.set(courseIndex, { name: courseName, lines: [] });
        } else if (!printerCourses.get(courseIndex).name && courseName) {
          printerCourses.get(courseIndex).name = courseName;
        }
        printerCourses.get(courseIndex).lines.push(line);
      });
    };

    (Array.isArray(cart) ? cart : []).forEach((item) => {
      const product = prodotti.find((p) => Number(p.id) === Number(item.productId));
      if (!product) return;
      const variantNames = Array.isArray(item.variantSnapshots)
        ? item.variantSnapshots.map((variant) => variant?.name).filter(Boolean)
        : [];
      const variantSuffix = variantNames.length ? ` + ${variantNames.join(' + ')}` : '';
      const quantityLabel = formatQuantityWithUnit(item.quantity, product);
      const line = appendPrintNote(formatCommandItem(quantityLabel, `${product.name}${variantSuffix}`), item.note);
      addLineToPrinters(getProductPrinterTargets(product), defaultCourseIndex, '', line);
    });

    selectedPackages.forEach((selectedPackage) => {
      const pkg = availablePackages.find((p) => Number(p.id) === Number(selectedPackage.packageId));
      if (!pkg) return;
      const packageQty = Number(selectedPackage.quantity || 0);
      if (packageQty <= 0) return;

      if (isTastingMenu(pkg)) {
        getOrderedPackageItems(pkg).forEach((item) => {
          const product = item?.product || prodotti.find((p) => Number(p.id) === Number(item?.productId));
          const itemName = getPackageItemName(item, prodotti, t('newproduct'));
          const variantName = getPackageItemVariantName(item);
          const variantSuffix = variantName ? ` + ${variantName}` : '';
          const itemQty = Number(item?.quantity || 0) * packageQty;
          const course = getCourseData(item);
          const line = appendPrintNote(
            formatCommandItem(itemQty, `${itemName}${variantSuffix} [${pkg.name}]`),
            item.note,
            selectedPackage.note
          );
          addLineToPrinters(
            getProductPrinterTargets(product),
            course.index,
            course.name,
            line
          );
        });
        return;
      }

      const packagePrinterTargets = new Set();
      (Array.isArray(pkg.items) ? pkg.items : []).forEach((item) => {
        const product = item?.product || prodotti.find((p) => Number(p.id) === Number(item?.productId));
        getProductPrinterTargets(product).forEach((printerName) => packagePrinterTargets.add(printerName));
      });

      addLineToPrinters(
        [...packagePrinterTargets],
        defaultCourseIndex,
        '',
        appendPrintNote(formatCommandItem(packageQty, `[Pacchetto] ${pkg.name}`), selectedPackage.note)
      );
    });

    fixedMenuDrafts.forEach((draft) => {
      (draft.selections || []).forEach((selection) => {
        const product = prodotti.find((item) => Number(item.id) === Number(selection.productId));
        const supplement = Number(selection.extraPrice || 0);
        const selectionQuantity = Number(selection.quantity || 1) * Number(draft.quantity || 1);
        addLineToPrinters(
          getProductPrinterTargets(product),
          Number(selection.courseIndex) || 1,
          selection.groupName || '',
          appendPrintNote(formatCommandItem(selectionQuantity, `${selection.productName}`
          + `${supplement > 0 ? ` +EUR ${supplement.toFixed(2)}` : ''}`
          + `${showFixedMenuNameOnKitchenPrint ? ` [${draft.menuName}]` : ''}`), selection.note, draft.note)
        );
      });
    });

    const toDocument = (title, courseGroups) => {
      const docLines = [];
      [...courseGroups.entries()]
        .sort(([a], [b]) => a - b)
        .forEach(([courseIndex, group], groupIndex, groups) => {
          docLines.push(formatCourseHeading(courseIndex, group.name));
          group.lines.forEach((line) => docLines.push(line));
          docLines.push(formatCourseSeparator(groupIndex < groups.length - 1));
        });
      return {
        kind: 'KITCHEN_ORDER',
        fontSize: orderPrintFontSize,
        title,
        tableName: activeTable?.name || '-',
        tableNote: activeTable?.note || '',
        customerName: normalizePrintCustomer(
          activeReservationLabel || activeTable?.nomeCliente || selectedTavolo?.nomeCliente || tavolo?.nomeCliente
        ),
        date: formatDateTime24(new Date()),
        body: docLines.filter(Boolean).join('\r\n'),
      };
    };

    const targets = Array.from(linesByPrinter.entries()).map(([printerName, printerLines]) => ({
      printerName,
      content: toDocument('', printerLines),
    }));

    return {
      targets,
      unassignedItems,
      unassignedContent: null,
    };
  }, [
    activeCourseOrders.length,
    availablePackages,
    cart,
    fixedMenuDrafts,
    formatQuantityWithUnit,
    getProductPrinterTargets,
    prodotti,
    selectedPackages,
    selectedTargetCourseIndex,
    activeReservationLabel,
    selectedTavolo?.nomeCliente,
    tavolo?.nomeCliente,
    showFixedMenuNameOnKitchenPrint,
    orderPrintFontSize,
    t,
  ]);

  const buildFullReprintContent = useCallback((activeTable) => {
    const nowLabel = formatDateTime24(new Date());
    const courseGroups = new Map();
    const lines = [
      'TIPO STAMPA: RISTAMPA COMANDA',
      `TAVOLO: ${activeTable?.name || '-'}`,
      `DATA: ${nowLabel}`,
      '================================',
    ];

    let grandTotal = 0;

    const addCourseLine = (courseIndex, courseName, line) => {
      if (!courseGroups.has(courseIndex)) {
        courseGroups.set(courseIndex, { name: courseName, lines: [] });
      } else if (!courseGroups.get(courseIndex).name && courseName) {
        courseGroups.get(courseIndex).name = courseName;
      }
      courseGroups.get(courseIndex).lines.push(line);
    };

    activeCourseOrders.forEach((order, orderIndex) => {
      const orderCourseIndex = orderIndex + 1;

      (Array.isArray(order?.orderProducts) ? order.orderProducts : []).forEach((row) => {
        const product = row?.product;
        const variantNames = Array.isArray(row?.orderProductVariants)
          ? row.orderProductVariants.map((entry) => entry?.variant?.name).filter(Boolean)
          : [];
        const variantSuffix = variantNames.length ? ` + ${variantNames.join(' + ')}` : '';
        const quantityLabel = formatQuantityWithUnit(row?.quantity, product);
        const unitPrice = Number(row?.unitPrice ?? product?.price ?? 0);
        const rowTotal = unitPrice * Number(row?.quantity || 0);
        grandTotal += rowTotal;

        addCourseLine(
          orderCourseIndex,
          '',
          `- ${(product?.name || t('newproduct'))}${variantSuffix} x${quantityLabel}  EUR ${rowTotal.toFixed(2)}`
        );
      });

      (Array.isArray(order?.orderPackages) ? order.orderPackages : []).forEach((row) => {
        const packageName = row?.package?.name || `${t('orderPackages.label')} ${row?.packageId || ''}`.trim();
        const quantity = Number(row?.quantity || 0);
        const unitPrice = Number(row?.unitPrice ?? row?.package?.price ?? 0);
        const rowTotal = unitPrice * quantity;
        grandTotal += rowTotal;

        if (isTastingMenu(row?.package)) {
          getOrderPackageItems(row).forEach((item) => {
            const course = getCourseData(item);
            const itemName = getPackageItemName(item, prodotti, t('newproduct'));
            const variantName = getPackageItemVariantName(item);
            const itemQty = Number(item?.quantity || 0) * quantity;
            addCourseLine(
              course.index,
              course.name,
              `- ${itemName}${variantName ? ` + ${variantName}` : ''} x${itemQty} [${packageName}]`
            );
          });
        } else {
          addCourseLine(orderCourseIndex, '', `- ${packageName} x${quantity}  EUR ${rowTotal.toFixed(2)}`);
        }
      });
    });

    [...courseGroups.entries()]
      .sort(([a], [b]) => a - b)
      .forEach(([courseIndex, group]) => {
        lines.push(`${courseIndex} - ${group.name || 'PORTATA'}`);
        lines.push('--------------------------------');
        lines.push(...group.lines);
        lines.push('');
      });

    lines.push('================================');
    lines.push(`TOTALE RISTAMPA: EUR ${grandTotal.toFixed(2)}`);

    return lines.join('\r\n');
  }, [activeCourseOrders, formatQuantityWithUnit, prodotti, t]);

  const buildPrinterTargetsForReprint = useCallback((activeTable) => {
    const linesByPrinter = new Map();
    const unassignedItems = [];

    const addLineToPrinters = (printerNames, courseIndex, courseName, line) => {
      if (!Array.isArray(printerNames) || printerNames.length === 0) {
        unassignedItems.push(line);
        return;
      }
      printerNames.forEach((printerName) => {
        if (!linesByPrinter.has(printerName)) linesByPrinter.set(printerName, new Map());
        const printerCourses = linesByPrinter.get(printerName);
        if (!printerCourses.has(courseIndex)) {
          printerCourses.set(courseIndex, { name: courseName, lines: [] });
        } else if (!printerCourses.get(courseIndex).name && courseName) {
          printerCourses.get(courseIndex).name = courseName;
        }
        printerCourses.get(courseIndex).lines.push(line);
      });
    };

    activeCourseOrders.forEach((order, orderIndex) => {
      const orderCourseIndex = orderIndex + 1;
      (Array.isArray(order?.orderProducts) ? order.orderProducts : []).forEach((row) => {
        const product = row?.product;
        if (!product) return;
        const variantNames = Array.isArray(row?.orderProductVariants)
          ? row.orderProductVariants.map((entry) => entry?.variant?.name).filter(Boolean)
          : [];
        const variantSuffix = variantNames.length ? ` + ${variantNames.join(' + ')}` : '';
        const quantityLabel = formatQuantityWithUnit(row?.quantity, product);
        const line = appendPrintNote(formatCommandItem(quantityLabel, `${product.name}${variantSuffix}`), row.note);
        addLineToPrinters(getProductPrinterTargets(product), orderCourseIndex, '', line);
      });

      (Array.isArray(order?.orderPackages) ? order.orderPackages : []).forEach((row) => {
        const packageName = row?.package?.name || `${t('orderPackages.label')} ${row?.packageId || ''}`.trim();
        const quantity = Number(row?.quantity || 0);
        if (quantity <= 0) return;

        if (isTastingMenu(row?.package)) {
          getOrderPackageItems(row).forEach((item) => {
            const product = item?.product || prodotti.find((p) => Number(p.id) === Number(item?.productId));
            const itemName = getPackageItemName(item, prodotti, t('newproduct'));
            const variantName = getPackageItemVariantName(item);
            const itemQty = Number(item?.quantity || 0) * quantity;
            const course = getCourseData(item);
            const line = appendPrintNote(
              formatCommandItem(itemQty, `${itemName}${variantName ? ` + ${variantName}` : ''} [${packageName}]`),
              item.note,
              row.note
            );
            addLineToPrinters(
              getProductPrinterTargets(product),
              course.index,
              course.name,
              line
            );
          });
          return;
        }

        const packagePrinterTargets = new Set();
        getOrderPackageItems(row).forEach((item) => {
          const product = item?.product || prodotti.find((p) => Number(p.id) === Number(item?.productId));
          getProductPrinterTargets(product).forEach((printerName) => packagePrinterTargets.add(printerName));
        });

        const line = appendPrintNote(formatCommandItem(quantity, `[Pacchetto] ${packageName}`), row.note);
        addLineToPrinters([...packagePrinterTargets], orderCourseIndex, '', line);
      });

      (Array.isArray(order?.orderFixedPriceMenus) ? order.orderFixedPriceMenus : []).forEach((menuRow) => {
        (Array.isArray(menuRow?.selections) ? menuRow.selections : []).forEach((selection) => {
          const product = selection?.product;
          const supplement = Number(selection?.extraPriceSnapshot || 0);
          const selectionQuantity = Number(selection?.quantity || 1) * Number(menuRow?.quantity || 1);
          const line = appendPrintNote(formatCommandItem(selectionQuantity,
            `${selection?.productNameSnapshot || product?.name || t('newproduct')}`
            + `${supplement > 0 ? ` +EUR ${supplement.toFixed(2)}` : ''}`
            + `${showFixedMenuNameOnKitchenPrint ? ` [${menuRow?.menuNameSnapshot || 'Menu'}]` : ''}`), selection.note, menuRow.note);
          addLineToPrinters(
            getProductPrinterTargets(product),
            Number(selection?.courseIndex) || 1,
            selection?.groupNameSnapshot || '',
            line
          );
        });
      });
    });

    const toDocument = (title, groupedLines) => {
      const docLines = [];
      [...groupedLines.entries()]
        .sort(([a], [b]) => a - b)
        .forEach(([courseIndex, group], groupIndex, groups) => {
          docLines.push(formatCourseHeading(courseIndex, group.name));
          group.lines.forEach((line) => docLines.push(line));
          docLines.push(formatCourseSeparator(groupIndex < groups.length - 1));
        });
      return {
        kind: 'KITCHEN_ORDER',
        fontSize: orderPrintFontSize,
        title,
        tableName: activeTable?.name || '-',
        tableNote: activeTable?.note || '',
        customerName: normalizePrintCustomer(
          activeReservationLabel || activeTable?.nomeCliente || selectedTavolo?.nomeCliente || tavolo?.nomeCliente
        ),
        date: formatDateTime24(new Date()),
        body: docLines.join('\r\n'),
      };
    };

    const targets = Array.from(linesByPrinter.entries()).map(([printerName, printerLines]) => ({
      printerName,
      content: toDocument('TIPO STAMPA: RISTAMPA COMANDA', printerLines),
    }));

    return {
      targets,
      unassignedItems,
      unassignedContent: null,
    };
  }, [activeCourseOrders, formatQuantityWithUnit, getProductPrinterTargets, prodotti, t,
    showFixedMenuNameOnKitchenPrint, orderPrintFontSize, activeReservationLabel, selectedTavolo?.nomeCliente, tavolo?.nomeCliente]);

  const fetchData = useCallback(async () => {
    if (!hasLoadedCatalog) setCatalogLoading(true);
    try {
      const [prodottiData, tavoliData, categorieData, categoryOrder, categoryVisibility, subCategorieData, packagesData, ordersData, fixedMenuData] = await Promise.all([
        getAllProdottiByNow(token, tenant),
        getAllTavoli(token, tenant, { noCache: true }),
        getAllCategorie(token, tenant),
        String(tenantType || '').toUpperCase() === 'RISTORANTE'
          ? getMyCategoryOrder(token, tenant)
          : Promise.resolve([]),
        String(tenantType || '').toUpperCase() === 'RISTORANTE'
          ? getMyCategoryVisibility(token, tenant)
          : Promise.resolve({ configured: false, visibleCategoryIds: [] }),
        getAllSottocategorie(token, tenant),
        getAllPackagesByNow(token, tenant),
        getAllOrdini(token, tenant, { noCache: true }),
        String(tenantType || '').toUpperCase() === 'RISTORANTE'
          ? getAllFixedPriceMenus(token, tenant)
          : Promise.resolve([]),
      ]);
      const visibleCatalog = applyUserCategoryVisibility(categorieData, prodottiData, categoryVisibility);
      setProdotti(visibleCatalog.products);
      setTavoli(tavoliData.sort((a, b) => a.roomId - b.roomId));
      setCategorie(applyCategoryOrder(visibleCatalog.categories, categoryOrder));
      setSottocategorie(subCategorieData);
      setAvailablePackages(Array.isArray(packagesData) ? packagesData : []);
      setOrders(Array.isArray(ordersData) ? ordersData : []);
      const activeMenus = (Array.isArray(fixedMenuData) ? fixedMenuData : [])
        .filter((menu) => menu?.active)
        .sort((a, b) => Number(a.id) - Number(b.id));
      setActiveFixedPriceMenus(activeMenus);
      setActiveFixedPriceMenu((current) => (
        activeMenus.find((menu) => Number(menu.id) === Number(current?.id)) || null
      ));
    } catch (err) {
      showSnackbar(t('reservations_details.error') + err.message, 'error');
    } finally {
      setCatalogLoading(false);
      setHasLoadedCatalog(true);
    }
  }, [tenant, token, t, showSnackbar, hasLoadedCatalog, tenantType]);

  const openCategoryOrderDialog = useCallback(() => {
    setCategoryOrderDraft([...categorie]);
    setOpenCategoryOrder(true);
  }, [categorie]);

  const moveCategoryOrder = useCallback((index, offset) => {
    setCategoryOrderDraft((current) => {
      const targetIndex = index + offset;
      if (targetIndex < 0 || targetIndex >= current.length) return current;
      const next = [...current];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }, []);

  const persistCategoryOrder = useCallback(async () => {
    setSavingCategoryOrder(true);
    try {
      await saveMyCategoryOrder(categoryOrderDraft.map(({ id }) => Number(id)), token, tenant);
      setCategorie([...categoryOrderDraft]);
      setOpenCategoryOrder(false);
      showSnackbar('Ordine dei gruppi salvato', 'success');
    } catch (err) {
      showSnackbar(err?.message || 'Errore nel salvataggio dell\'ordine dei gruppi', 'error');
    } finally {
      setSavingCategoryOrder(false);
    }
  }, [categoryOrderDraft, token, tenant, showSnackbar]);

  useEffect(() => {
    fetchData();
    const cleanup = SetupWSEventListener(fetchData);
    return () => {
      cleanup();
    };
  }, [fetchData]);

  useEffect(() => {
    let isCancelled = false;
    let heartbeatId = null;
    const claimedTableIds = claimedTableIdsRef.current;
    const releaseClaimedSessions = () => {
      const tableIdsToRelease = [...claimedTableIds];
      claimedTableIds.clear();
      tableIdsToRelease.forEach((tableId) => {
        releaseTableSession(tableId, token, tenant, { keepalive: true }).catch(() => {});
      });
    };

    async function guardSessionOwnership() {
      if (!open || !tavolo?.id) return;

      try {
        await claimAndTrackTableSession(tavolo.id);
        if (isCancelled) {
          await releaseTableSession(tavolo.id, token, tenant).catch(() => {});
          claimedTableIds.delete(Number(tavolo.id));
          return;
        }
        heartbeatId = window.setInterval(() => {
          [...claimedTableIds].forEach((tableId) => {
            claimTableSession(tableId, token, tenant).catch(() => {});
          });
        }, 20000);
      } catch (err) {
        if (!isCancelled) {
          showSnackbar(err?.message || String(err), err?.status === 409 ? 'warning' : 'error');
          onCloseRef.current?.();
        }
      }
    }

    guardSessionOwnership();
    window.addEventListener('pagehide', releaseClaimedSessions);

    return () => {
      isCancelled = true;
      if (heartbeatId) window.clearInterval(heartbeatId);
      window.removeEventListener('pagehide', releaseClaimedSessions);
      releaseClaimedSessions();
    };
  }, [open, tavolo?.id, token, tenant, showSnackbar, claimAndTrackTableSession]);

  useEffect(() => {
    if (!open) return;
    syncShowImagesFromBackend();
  }, [open, syncShowImagesFromBackend]);

  useEffect(() => {
    const bc = new BroadcastChannel('ws-events');
    const handler = ({ data }) => {
      if (data?.event === 'settings_updated') {
        syncShowImagesFromBackend(true);
      }
    };
    bc.addEventListener('message', handler);
    return () => {
      bc.removeEventListener('message', handler);
      bc.close();
    };
  }, [syncShowImagesFromBackend]);

  const handleQuantity = (id, index, value) => {
    if (Number(value) < 0) return;
    setQuantitaPeso((prev) => {
      const currentRows = Array.isArray(prev[id]) ? [...prev[id]] : [0];
      currentRows[index] = parseFloat(value) || 0;
      return { ...prev, [id]: currentRows };
    });
  };

  const addWeightRow = (id) => {
    setCercaPiatto('');
    setQuantitaPeso((prev) => {
      const currentRows = Array.isArray(prev[id]) ? [...prev[id]] : [];
      const newIndex = currentRows.length;
      const variantIds = getComboVariantIds(id);
      setWeightVariantRows((variantRows) => ({
        ...variantRows,
        [id]: {
          ...(variantRows[id] || {}),
          [newIndex]: variantIds,
        },
      }));
      return { ...prev, [id]: [...currentRows, 0] };
    });
  };

  const removeWeightRow = (id, index) => {
    setQuantitaPeso((prev) => {
      const currentRows = Array.isArray(prev[id]) ? [...prev[id]] : [];
      const nextRows = currentRows.filter((_, idx) => idx !== index);
      return { ...prev, [id]: nextRows };
    });
    setWeightVariantRows((prev) => {
      const currentRows = prev[id] || {};
      const nextRows = {};
      Object.entries(currentRows).forEach(([rowIndex, variantIds]) => {
        const numericIndex = Number(rowIndex);
        if (!Number.isInteger(numericIndex) || numericIndex === index) return;
        nextRows[numericIndex > index ? numericIndex - 1 : numericIndex] = variantIds;
      });
      return { ...prev, [id]: nextRows };
    });
  };

  const incrementWeightRow = (id, index) => {
    setCercaPiatto('');
    setQuantitaPeso((prev) => {
      const currentRows = Array.isArray(prev[id]) ? [...prev[id]] : [0];
      currentRows[index] = parseFloat((Number(currentRows[index] || 0) + 0.1).toFixed(2));
      return { ...prev, [id]: currentRows };
    });
  };

  const decrementWeightRow = (id, index) => {
    setQuantitaPeso((prev) => {
      const currentRows = Array.isArray(prev[id]) ? [...prev[id]] : [0];
      currentRows[index] = Math.max(parseFloat((Number(currentRows[index] || 0) - 0.1).toFixed(2)), 0);
      return { ...prev, [id]: currentRows };
    });
  };

  const incrementQuantity = (id, variantIds = []) => {
    setCercaPiatto('');
    const comboKey = buildComboKey(id, variantIds);
    setQuantita((prev) => ({ ...prev, [comboKey]: (prev[comboKey] || 0) + 1 }));
  };

  const decrementQuantity = (id, variantIds = []) => {
    const comboKey = buildComboKey(id, variantIds);
    setQuantita((prev) => ({ ...prev, [comboKey]: Math.max((prev[comboKey] || 0) - 1, 0) }));
  };

  const selectProductOnce = useCallback((product) => {
    setCercaPiatto('');
    if (product?.pricePerWeight) {
      setQuantitaPeso((prev) => {
        const rows = Array.isArray(prev[product.id]) ? [...prev[product.id]] : [];
        if (rows.length === 0) rows.push(0);
        rows[0] = parseFloat((Number(rows[0] || 0) + 0.1).toFixed(2));
        return { ...prev, [product.id]: rows };
      });
      return;
    }
    const comboKey = buildComboKey(product.id, []);
    setQuantita((prev) => ({ ...prev, [comboKey]: Number(prev[comboKey] || 0) + 1 }));
  }, [buildComboKey]);

  const resetQuantity = () => {
    setQuantita({});
    setActiveVariantByProduct({});
    setQuantitaPeso({});
    setWeightVariantRows({});
    setPackageQuantita({});
    setFixedMenuDrafts([]);
    setFixedMenuSelections({});
    setOpenFixedMenu(false);
    setTargetCourseId('NEW');
  };

  useEffect(() => {
    const fetchReservation = async () => {
      if (!tavolo) return;
      try {
        const prenotazioni = await getAllPrenotazioni(token, tenant);
        const reservationsForTable = prenotazioni.filter(
          (r) => r.status === 'CONFIRMED'
            && r.tableReservations?.some((tr) => Number(tr?.tableId ?? tr?.table?.id) === Number(tavolo.id))
        );
        const now = new Date();
        const active = reservationsForTable.find(res => {
          const start = new Date(res.startDate);
          const end = new Date(res.endDate);
          const thirtyMinBefore = new Date(start);
          thirtyMinBefore.setMinutes(thirtyMinBefore.getMinutes() - 30);
          return now >= thirtyMinBefore && now <= end;
        });
        const today = new Date(now);
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const nextTodayReservation = reservationsForTable
          .filter((res) => {
            const start = new Date(res.startDate);
            const end = new Date(res.endDate);
            return start >= today && start < tomorrow && end >= now;
          })
          .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0];
        const reservationForSeats = active || nextTodayReservation || null;
        const tableReservationMarker = reservationForSeats
          ? getNonReservationTableMarkers(tenant).has(`${Number(tavolo.id)}:${Number(reservationForSeats.id)}`)
          : false;

        setActiveReservation(reservationForSeats);
        setConfirmReservationSeats(Boolean(reservationForSeats) && !tableReservationMarker);
        setIsNonReservationClientSelection(tableReservationMarker);
        const confirmedCovers = confirmedCoversRef.current;
        if (Number(confirmedCovers.tableId) !== Number(tavolo.id) || !confirmedCovers.count) {
          setNumeroCoperti(getDefaultSeatsForContext(reservationForSeats, tavolo));
        }
      } catch (err) {
        showSnackbar(t('booking.error') + err.message, 'error');
      }
    };
    fetchReservation();
  }, [tavolo, t, showSnackbar, token, tenant, getDefaultSeatsForContext]);

  useEffect(() => {
    if (!confirmReservationSeats) return;
    const reservationId = Number(activeReservation?.id);
    if (!Number.isInteger(reservationId) || reservationId <= 0) return;
    if (autoAppliedReservationIdRef.current === reservationId) return;
    if (!Array.isArray(availablePackages) || availablePackages.length === 0) return;

    const bookedPackageIds = activeReservationPackageIds;
    if (bookedPackageIds.length === 0) {
      autoAppliedReservationIdRef.current = reservationId;
      autoAddedPackageIdsRef.current = [];
      return;
    }

    setPackageQuantita((prev) => {
      const next = { ...prev };
      bookedPackageIds.forEach((packageId) => {
        const existsInCatalog = availablePackages.some((pkg) => Number(pkg?.id) === packageId);
        if (existsInCatalog && (!next[packageId] || Number(next[packageId]) <= 0)) {
          next[packageId] = 1;
        }
      });
      return next;
    });

    autoAppliedReservationIdRef.current = reservationId;
    autoAddedPackageIdsRef.current = bookedPackageIds;
  }, [activeReservation, activeReservationPackageIds, availablePackages, confirmReservationSeats]);

  useEffect(() => {
    if (confirmReservationSeats) return;
    removeAutoAddedReservationPackages();
  }, [confirmReservationSeats, removeAutoAddedReservationPackages]);

  useEffect(() => {
    const nuovoCarrello = prodotti.flatMap((p) => {
      const righe = [];
      const weightRows = p.pricePerWeight
        ? (Array.isArray(quantitaPeso[p.id]) ? quantitaPeso[p.id] : [])
        : [];

      weightRows.forEach((weightQty, index) => {
        const baseQuantity = Number(weightQty || 0);
        if (baseQuantity <= 0) return;
        const variantIds = normalizeVariantIds(weightVariantRows[p.id]?.[index]);
        const variantSnapshots = getVariantSnapshotsForProduct(p, variantIds);
        righe.push({
          productId: p.id,
          variantIds,
          variantSnapshots,
          comboKey: `weight:${p.id}:${index}`,
          isWeight: true,
          weightRowIndex: index,
          quantity: baseQuantity,
          note: note[p.id] || '',
        });
      });

      const comboPrefix = `${Number(p.id)}:`;
      Object.entries(quantita).forEach(([key, qty]) => {
        if (!key.startsWith(comboPrefix) || Number(qty) <= 0) return;
        const variantPart = key.split(':')[1] || 'base';
        const variantIds = variantPart === 'base'
          ? []
          : variantPart.split('-').map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0);
        const variantSnapshots = variantIds
          .map((variantId) => (Array.isArray(p.variants) ? p.variants.find((v) => Number(v.id) === variantId) : null))
          .filter(Boolean);
        righe.push({
          productId: p.id,
          variantIds,
          variantSnapshots,
          comboKey: key,
          isWeight: false,
          quantity: Number(qty),
          note: note[p.id] || '',
        });
      });
      return righe;
    });
    setCart(nuovoCarrello);
  }, [getVariantSnapshotsForProduct, normalizeVariantIds, note, quantita, quantitaPeso, prodotti, weightVariantRows]);

  const getOrderLineVariantIds = useCallback((line) => {
    if (Array.isArray(line?.variantIds)) return normalizeVariantIds(line.variantIds);
    if (Array.isArray(line?.orderProductVariants)) {
      return normalizeVariantIds(line.orderProductVariants.map((opv) => opv?.variantId ?? opv?.variant?.id));
    }
    const single = Number(line?.variantId ?? line?.variant?.id);
    return Number.isInteger(single) && single > 0 ? [single] : [];
  }, [normalizeVariantIds]);

  const areSameProductLine = useCallback((a, b) => {
    if (Number(a?.productId ?? a?.product?.id) !== Number(b?.productId ?? b?.product?.id)) return false;
    if (a?.isWeight || b?.isWeight || a?.product?.pricePerWeight || b?.product?.pricePerWeight) return false;
    return sameVariantIds(getOrderLineVariantIds(a), getOrderLineVariantIds(b));
  }, [getOrderLineVariantIds, sameVariantIds]);

  const toOrderUpdateProductLine = useCallback((line) => {
    const id = Number(line?.id ?? line?.orderProductId);
    return {
      ...(Number.isInteger(id) && id > 0 ? { id } : {}),
      productId: Number(line?.productId ?? line?.product?.id),
      variantIds: getOrderLineVariantIds(line),
      quantity: Number(line?.quantity) || 0,
      ...(line?.discount !== undefined && line?.discount !== null ? { discount: Number(line.discount) } : {}),
      ...(typeof line?.note === 'string' ? { note: line.note } : {}),
    };
  }, [getOrderLineVariantIds]);

  const toOrderUpdatePackageLine = useCallback((line) => {
    const id = Number(line?.id ?? line?.orderPackageId);
    return {
      ...(Number.isInteger(id) && id > 0 ? { id } : {}),
      packageId: Number(line?.packageId ?? line?.package?.id),
      quantity: Number(line?.quantity) || 0,
      ...(line?.discount !== undefined && line?.discount !== null ? { discount: Number(line.discount) } : {}),
      ...(typeof line?.note === 'string' ? { note: line.note } : {}),
    };
  }, []);

  const mergeProductsIntoCourse = useCallback((existingProducts = [], incomingProducts = []) => {
    const merged = [...(Array.isArray(existingProducts) ? existingProducts : [])];
    (Array.isArray(incomingProducts) ? incomingProducts : []).forEach((incoming) => {
      const existingIndex = merged.findIndex((line) => areSameProductLine(line, incoming));
      if (existingIndex >= 0) {
        merged[existingIndex] = {
          ...merged[existingIndex],
          quantity: Number(merged[existingIndex]?.quantity || 0) + Number(incoming?.quantity || 0),
        };
      } else {
        merged.push(incoming);
      }
    });
    return merged.map(toOrderUpdateProductLine)
      .filter((line) => Number.isInteger(line.productId) && line.productId > 0 && line.quantity > 0);
  }, [areSameProductLine, toOrderUpdateProductLine]);

  const mergePackagesIntoCourse = useCallback((existingPackages = [], incomingPackages = []) => {
    const merged = [...(Array.isArray(existingPackages) ? existingPackages : [])];
    (Array.isArray(incomingPackages) ? incomingPackages : []).forEach((incoming) => {
      const incomingId = Number(incoming?.packageId ?? incoming?.package?.id);
      const existingIndex = merged.findIndex((line) => Number(line?.packageId ?? line?.package?.id) === incomingId);
      if (existingIndex >= 0) {
        merged[existingIndex] = {
          ...merged[existingIndex],
          quantity: Number(merged[existingIndex]?.quantity || 0) + Number(incoming?.quantity || 0),
        };
      } else {
        merged.push(incoming);
      }
    });
    return merged.map(toOrderUpdatePackageLine)
      .filter((line) => Number.isInteger(line.packageId) && line.packageId > 0 && line.quantity > 0);
  }, [toOrderUpdatePackageLine]);

  const handleCart = async (
    cartProducts = [],
    cartPackages = [],
    cartFixedMenus = [],
    destinationCourseId = targetCourseId
  ) => {
    const token = localStorage.getItem('accessToken');
    const tenant = localStorage.getItem('tenantId');

    const tableId = tavolo?.id || selectedTavolo?.id;

    if (!tableId) {
      handleAlert(t("Nessun tavolo selezionato!"), "error");
      return;
    }

    await claimAndTrackTableSession(tableId);

    const currentTable = tavolo
      || selectedTavolo
      || (Array.isArray(tavoli) ? tavoli.find((tb) => Number(tb.id) === Number(tableId)) : null);

    let productsForOrder = Array.isArray(cartProducts) ? [...cartProducts] : [];
    const allOrders = await getAllOrdini(token, tenant, { noCache: true });
    const copertoProduct = getCopertoProductForTable(currentTable);
    const confirmedCoverCount = Number(confirmedCoversRef.current.tableId) === Number(tableId)
      ? confirmedCoversRef.current.count
      : null;
    let coverQtyForTable = resolveOrderCoverQuantity({
      confirmedQuantity: confirmedCoverCount,
      enteredQuantity: numeroCoperti,
      tableTakenSeats: currentTable?.takenSeats,
      tableMaxSeats: currentTable?.maxSeats,
    });

    if (copertoProduct) {
      const activeTableOrders = (Array.isArray(allOrders) ? allOrders : []).filter(
        (ord) => orderBelongsToTable(ord, tableId)
          && ord?.status !== 'PAID'
          && ord?.status !== 'CANCELLED'
      );

      const copertoComboKey = buildComboKey(copertoProduct.id, []);
      const draftCopertoLine = productsForOrder.find((p) => (
        Number(p?.productId) === Number(copertoProduct.id)
        && (!Array.isArray(p?.variantIds) || p.variantIds.length === 0)
      ));
      const existingCoperto = activeTableOrders
        .flatMap((ord) => (Array.isArray(ord?.orderProducts) ? ord.orderProducts.map((op) => ({
          orderId: ord.id,
          orderProductId: Number(op?.id),
          productId: Number(op?.productId ?? op?.product?.id),
          quantity: Number(op?.quantity) || 0,
        })) : []))
        .find((op) => op.productId === Number(copertoProduct.id));

      const copertoQty = resolveOrderCoverQuantity({
        existingQuantity: existingCoperto?.quantity,
        confirmedQuantity: confirmedCoverCount,
        draftQuantity: draftCopertoLine?.quantity ?? quantita[copertoComboKey],
        enteredQuantity: numeroCoperti,
        tableTakenSeats: currentTable?.takenSeats,
        tableMaxSeats: currentTable?.maxSeats,
      });
      coverQtyForTable = copertoQty;

      if (existingCoperto) {
        productsForOrder = productsForOrder.filter(
          (p) => Number(p?.productId) !== Number(copertoProduct.id)
        );
      } else {
        const copertoIndex = productsForOrder.findIndex(
          (p) => Number(p?.productId) === Number(copertoProduct.id)
        );

        if (copertoIndex >= 0) {
          productsForOrder[copertoIndex] = {
            ...productsForOrder[copertoIndex],
            variantIds: Array.isArray(productsForOrder[copertoIndex]?.variantIds)
              ? productsForOrder[copertoIndex].variantIds
              : [],
            quantity: copertoQty,
            note: productsForOrder[copertoIndex]?.note || '',
          };
        } else {
          productsForOrder.push({
            productId: copertoProduct.id,
            variantIds: [],
            quantity: copertoQty,
            note: '',
          });
        }
      }
    }

    const additionalTablesForOrder = addTableFlag
      ? buildTableGuestAllocations(currentTable, idTables, coverQtyForTable)
        .filter((entry) => Number(entry.tableId) !== Number(tableId))
        .map((entry) => ({
          tableId: entry.tableId,
          ...(entry.guestCount > 0 ? { guestCount: entry.guestCount } : {}),
        }))
      : [];

    const normalizedProductsForOrder = (Array.isArray(productsForOrder) ? productsForOrder : [])
      .map((p) => {
        const variantIds = Array.isArray(p.variantIds)
          ? p.variantIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
          : [];

        return {
          productId: Number(p.productId),
          variantIds,
          quantity: Number(p.quantity) || 0,
          note: typeof p.note === 'string' ? p.note : '',
        };
      })
      .filter((p) => Number.isInteger(p.productId) && p.productId > 0 && p.quantity > 0);

    const normalizedFixedMenus = (Array.isArray(cartFixedMenus) ? cartFixedMenus : [])
      .map((menu) => ({
        fixedPriceMenuId: Number(menu.fixedPriceMenuId),
        quantity: Math.max(1, Number(menu.quantity) || 1),
        selections: (menu.selections || []).map((selection) => ({
          groupId: Number(selection.groupId),
          courseIndex: Number(selection.courseIndex),
          productId: Number(selection.productId),
          quantity: Math.max(1, Number(selection.quantity) || 1),
        })),
      }))
      .filter((menu) => (
        Number.isInteger(menu.fixedPriceMenuId)
        && menu.fixedPriceMenuId > 0
        && menu.selections.length > 0
      ));

    // The server resolves the current catalog price. Sending the display-only
    // unitPrice would incorrectly turn a normal package into a price override.
    const normalizedPackagesForOrder = (Array.isArray(cartPackages) ? cartPackages : [])
      .map((pkg) => ({
        packageId: Number(pkg?.packageId ?? pkg?.package?.id),
        quantity: Number(pkg?.quantity) || 0,
        ...(pkg?.discount !== undefined && pkg?.discount !== null
          ? { discount: Number(pkg.discount) }
          : {}),
        ...(typeof pkg?.note === 'string' ? { note: pkg.note } : {}),
        ...(Array.isArray(pkg?.items) ? { items: pkg.items } : {}),
      }))
      .filter((pkg) => Number.isInteger(pkg.packageId) && pkg.packageId > 0 && pkg.quantity > 0);

    const resolvedClientId =
      confirmReservationSeats && activeReservation?.clientId != null ? Number(activeReservation.clientId) :
        confirmReservationSeats && activeReservation?.client?.id != null ? Number(activeReservation.client.id) :
          null;
    const shouldClearReservationClient = !!activeReservation && !confirmReservationSeats;
    updateNonReservationTableMarker(tenant, tableId, activeReservation?.id, shouldClearReservationClient);

    const normalizedDestinationCourseId = Number(destinationCourseId);
    const destinationOrder = destinationCourseId !== 'NEW' && Number.isInteger(normalizedDestinationCourseId)
      ? (Array.isArray(allOrders) ? allOrders : []).find((order) =>
        Number(order?.id) === normalizedDestinationCourseId &&
        orderBelongsToTable(order, tableId) &&
        order?.status !== 'PAID' &&
        order?.status !== 'CANCELLED'
      )
      : null;

    if (destinationOrder && (normalizedProductsForOrder.length > 0 || normalizedPackagesForOrder.length > 0)) {
      const mergedProducts = mergeProductsIntoCourse(
        destinationOrder.orderProducts || [],
        normalizedProductsForOrder
      );
      const existingPackages = shouldClearReservationClient
        ? (destinationOrder.orderPackages || []).filter((line) => (
          !activeReservationPackageIds.includes(
            Number(line?.packageId ?? line?.package?.id)
          )
        ))
        : (destinationOrder.orderPackages || []);
      const mergedPackages = mergePackagesIntoCourse(existingPackages, normalizedPackagesForOrder);

      await aggiornaComanda(destinationOrder.id, {
        tableId: destinationOrder.tableId ?? tableId,
        ...(resolvedClientId !== null
          ? { clientId: resolvedClientId }
          : shouldClearReservationClient
            ? { clientId: null }
            : (destinationOrder.clientId != null ? { clientId: Number(destinationOrder.clientId) } : {})),
        totalDiscount: Number(destinationOrder.totalDiscount || 0),
        status: destinationOrder.status || 'OPEN',
        products: mergedProducts,
        packages: mergedPackages,
      }, token, tenant);
      updateNonReservationOrderMarker(tenant, destinationOrder.id, shouldClearReservationClient);
    } else if (!destinationOrder && (
      normalizedProductsForOrder.length > 0
      || normalizedPackagesForOrder.length > 0
      || normalizedFixedMenus.length > 0
    )) {
      const ordineCart = {
        tableId: tableId,
        clientId: resolvedClientId,
        totalDiscount: 0,
        ...(normalizedProductsForOrder.length > 0 ? { products: normalizedProductsForOrder } : {}),
        ...(normalizedPackagesForOrder.length > 0 ? { packages: normalizedPackagesForOrder } : {}),
        ...(normalizedFixedMenus.length > 0 ? { fixedPriceMenus: normalizedFixedMenus } : {}),
        ...(additionalTablesForOrder.length > 0 ? { additionalTables: additionalTablesForOrder } : {}),
      };
      const createdOrder = await creaComanda(ordineCart, token, tenant);
      updateNonReservationOrderMarker(tenant, createdOrder?.id, shouldClearReservationClient);
    }

    if (destinationOrder && normalizedFixedMenus.length > 0) {
      const createdMenuOrder = await creaComanda({
        tableId,
        clientId: resolvedClientId,
        totalDiscount: 0,
        fixedPriceMenus: normalizedFixedMenus,
        ...(additionalTablesForOrder.length > 0 ? { additionalTables: additionalTablesForOrder } : {}),
      }, token, tenant);
      updateNonReservationOrderMarker(tenant, createdMenuOrder?.id, shouldClearReservationClient);
    }

    await aggiornaTavolo(tableId, {
      status: 'OCCUPATO',
      takenSeats: coverQtyForTable,
    }, token, tenant);

    // update secondary tables too
    if (Array.isArray(idTables) && idTables.length > 1) {
      for (const tab of idTables) {
        if (tab.id === tableId) continue;
        await aggiornaTavolo(tab.id, { status: 'OCCUPATO' }, token, tenant);
      }
    }

    handleAlert(
      getTenantTranslation(t, tenantType, "selectedOrders_alert.orderSaved"),
      "success"
    );
  };

  async function handleSendOrder(cartProducts = [], cartPackages = [], cartFixedMenus = []) {
    if (open) {
      try {
        setIsSendingOrder(true);
        await handleCart(cartProducts, cartPackages, cartFixedMenus, targetCourseId);
        orderSentRef.current = true;
        onOrderSent?.();
        window.dispatchEvent(new CustomEvent('comanda-creata'));
        resetQuantity();
      } catch (err) {
        handleAlert(
          err?.status === 409 ? err.message : t('reservations_details.error') + err.message,
          err?.status === 409 ? 'warning' : 'error'
        );
      } finally {
        setIsSendingOrder(false);
      }
    } else {
      setShowSummary(true);
    }
  }

  async function handlePrintDifferential() {
    const activeTable = resolveActiveTableContext();
    if (!activeTable?.id) {
      handleAlert(t('Nessun tavolo selezionato!'), 'error');
      return;
    }
    if (liveDraftItems.length === 0 && activeCourseOrders.length === 0) {
      handleAlert('Nessuna comanda da stampare', 'warning');
      return;
    }

    try {
      setIsPrinting(true);
      if (liveDraftItems.length > 0) {
        await handleCart(cart, selectedPackages, fixedMenuDrafts, targetCourseId);
        orderSentRef.current = true;
        onOrderSent?.();
        window.dispatchEvent(new CustomEvent('comanda-creata'));
        resetQuantity();
      }

      // Always print from a fresh server snapshot. Mobile websocket/state updates can
      // arrive after the operator presses STAMPA immediately following several Segue.
      const freshOrders = await getAllOrdini(token, tenant, { noCache: true });
      const today = new Date();
      const ordersToPrint = (freshOrders || [])
        .filter((order) => {
          if (!orderBelongsToTable(order, activeTable.id)) return false;
          if (order?.status === 'PAID' || order?.status === 'CANCELLED') return false;
          const createdAt = new Date(order?.createdAt);
          return !Number.isNaN(createdAt.getTime())
            && createdAt.getFullYear() === today.getFullYear()
            && createdAt.getMonth() === today.getMonth()
            && createdAt.getDate() === today.getDate();
        })
        .sort(compareOrdersByCourse);
      setOrders(freshOrders);

      const currentLines = normalizeKitchenLines(ordersToPrint, {
        showFixedMenuName: showFixedMenuNameOnKitchenPrint,
        roomId: activeTable?.roomId ?? activeTable?.room?.id,
      });
      const storedState = await getKitchenPrintState(activeTable.id, token, tenant);
      const diff = buildKitchenDiff(currentLines, storedState?.snapshot);
      if (diff.lines.length === 0) {
        handleAlert('Nessuna variazione da stampare', 'info');
        return;
      }

      const { targets, unassignedItems } = buildKitchenTargets(diff.lines, {
        tableName: activeTable.name,
        tableNote: activeTable.note,
        customerLabel: activeReservationLabel
          || activeTable?.nomeCliente
          || selectedTavolo?.nomeCliente
          || tavolo?.nomeCliente,
        fontSize: orderPrintFontSize,
      });

      if (targets.length === 0) {
        handleAlert('Nessuna stampante articolo configurata per la comanda', 'error');
        return;
      }
      if (unassignedItems.length > 0) {
        const missing = unassignedItems.slice(0, 5).join(', ');
        const suffix = unassignedItems.length > 5 ? ' ...' : '';
        handleAlert(`Stampa bloccata: associa una stampante a tutti gli articoli. Mancanti: ${missing}${suffix}`, 'error');
        return;
      }

      await printDocument({
        targets,
        action: 'print_differential',
      }, token, tenant);
      await saveKitchenPrintState(activeTable.id, makeKitchenSnapshot(currentLines), token, tenant);
      handleAlert(diff.firstPrint ? 'Comanda inviata in stampa' : 'Variazioni inviate in stampa', 'success');
    } catch (err) {
      handleAlert(`${t('reservations_details.error')}${err?.message || err}`, 'error');
    } finally {
      setIsPrinting(false);
    }
  }

  async function handleReprintFullOrder() {
    const activeTable = resolveActiveTableContext();
    if (!activeTable?.id) {
      handleAlert(t('Nessun tavolo selezionato!'), 'error');
      return;
    }
    if (!Array.isArray(activeCourseOrders) || activeCourseOrders.length === 0) {
      handleAlert('Nessuna comanda da ristampare', 'warning');
      return;
    }

    try {
      setIsPrinting(true);
      const { targets, unassignedItems } = buildPrinterTargetsForReprint(activeTable);

      if (targets.length === 0) {
        handleAlert('Nessuna stampante articolo configurata per i prodotti della comanda', 'error');
        return;
      }
      if (unassignedItems.length > 0) {
        const missing = unassignedItems.slice(0, 5).join(', ');
        const suffix = unassignedItems.length > 5 ? ' ...' : '';
        handleAlert(`Ristampa bloccata: associa una stampante a tutti gli articoli. Mancanti: ${missing}${suffix}`, 'error');
        return;
      }

      await printDocument({
        targets,
        action: 'print_reprint',
      }, token, tenant);
      handleAlert('Ristampa comanda inviata', 'success');
    } catch (err) {
      handleAlert(`${t('reservations_details.error')}${err?.message || err}`, 'error');
    } finally {
      setIsPrinting(false);
    }
  }

  const handleClose = async () => {
    const reservations = await getAllPrenotazioni(token, tenant).catch(() => []);
    const now = new Date();
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);

    const hasReservation = (tableId) => reservations.some(p => {
      const start = new Date(p.startDate);
      const end = p.endDate ? new Date(p.endDate) : null;
      return p.status === 'CONFIRMED'
        && p.tableReservations?.some(tr => (tr.tableId ?? tr.table?.id) === tableId)
        && start >= today && start < tomorrow
        && (end ? end > now : start > now);
    });

    // Case: table was occupied but no order was sent → revert everything
    if (tableWasOccupiedRef.current && !orderSentRef.current && tavolo?.id) {
      try {
        const primaryStatus = hasReservation(tavolo.id) ? 'RISERVATO' : 'LIBERO';
        await aggiornaTavolo(tavolo.id, { status: primaryStatus, takenSeats: 0 }, token, tenant);

        if (Array.isArray(idTables) && idTables.length > 1) {
          for (const tab of idTables) {
            if (tab.id === tavolo.id) continue;
            const secStatus = hasReservation(tab.id) ? 'RISERVATO' : 'LIBERO';
            await aggiornaTavolo(tab.id, { status: secStatus, takenSeats: 0 }, token, tenant);
            await releaseTableSession(tab.id, token, tenant);
          }
        }

        await releaseTableSession(tavolo.id, token, tenant);
      } catch (err) {
        console.error('Failed to revert table:', err);
      }
    }

    if (!orderSentRef.current && tavolo?.id && initialTableStatusRef.current !== 'OCCUPATO') {
      try {
        await releaseTableSession(tavolo.id, token, tenant);
      } catch (err) {
        console.error('Failed to release table session:', err);
      }
    }

    onClose?.();
  };

  const handleDeleteLiveDraftItem = useCallback((item) => {
    if (!item) return;

    if (item.kind === 'product') {
      if (item.isWeight) {
        const match = String(item.comboKey || '').match(/^weight:\d+:(\d+)$/);
        if (match) {
          const idx = Number(match[1]);
          if (Number.isInteger(idx) && idx >= 0) {
            setQuantitaPeso((prev) => {
              const currentRows = Array.isArray(prev[item.productId]) ? [...prev[item.productId]] : [];
              const nextRows = currentRows.filter((_, rowIdx) => rowIdx !== idx);
              return { ...prev, [item.productId]: nextRows };
            });
            setWeightVariantRows((prev) => {
              const currentRows = prev[item.productId] || {};
              const nextRows = {};
              Object.entries(currentRows).forEach(([rowIndex, variantIds]) => {
                const numericIndex = Number(rowIndex);
                if (!Number.isInteger(numericIndex) || numericIndex === idx) return;
                nextRows[numericIndex > idx ? numericIndex - 1 : numericIndex] = variantIds;
              });
              return { ...prev, [item.productId]: nextRows };
            });
            return;
          }
        }
        setQuantitaPeso((prev) => ({ ...prev, [item.productId]: [] }));
        setWeightVariantRows((prev) => ({ ...prev, [item.productId]: {} }));
        return;
      }
      if (item.comboKey) {
        setQuantita((prev) => ({ ...prev, [item.comboKey]: 0 }));
      }
      return;
    }

    if (item.kind === 'package' && item.packageId != null) {
      setPackageQuantita((prev) => ({ ...prev, [item.packageId]: 0 }));
      return;
    }

    if (item.kind === 'fixed-price-menu-selection') {
      setFixedMenuDrafts((current) => (
        current.filter((draft) => draft.clientId !== item.fixedMenuClientId)
      ));
    }
  }, []);

  const changeLiveDraftQuantity = useCallback((item, delta) => {
    if (!item || !delta) return;
    if (item.isWeight) {
      const match = String(item.comboKey || '').match(/^weight:\d+:(\d+)$/);
      if (!match) return;
      const rowIndex = Number(match[1]);
      setQuantitaPeso((prev) => {
        const rows = [...(Array.isArray(prev[item.productId]) ? prev[item.productId] : [])];
        rows[rowIndex] = Math.max(0, Number((Number(rows[rowIndex] || 0) + (delta * 0.1)).toFixed(2)));
        return { ...prev, [item.productId]: rows };
      });
      return;
    }
    setQuantita((prev) => ({
      ...prev,
      [item.comboKey]: Math.max(0, Number(prev[item.comboKey] || 0) + delta),
    }));
  }, []);

  const editLiveDraftNote = useCallback((item) => {
    const product = prodotti.find((entry) => Number(entry.id) === Number(item?.productId));
    if (!product) return;
    setProdottoNote(product);
    setOpenNote(true);
  }, [prodotti]);

  const editLiveDraftVariants = useCallback((item) => {
    const product = prodotti.find((entry) => Number(entry.id) === Number(item?.productId));
    if (!product) return;
    const selectedIds = normalizeVariantIds(item?.variantIds);
    const selectedVariants = getVariantSnapshotsForProduct(product, selectedIds).map((variant) => ({
      ...variant,
      productId: product.id,
      product,
    }));
    setVariantData((current) => [
      ...current.filter((variant) => Number(variant?.productId ?? variant?.product?.id) !== Number(product.id)),
      ...selectedVariants,
    ]);
    setActiveVariantByProduct((current) => ({
      ...current,
      [product.id]: selectedIds[selectedIds.length - 1] || null,
    }));
    setProdottoSelezionato(product);
    setOpenVariant(true);
  }, [getVariantSnapshotsForProduct, normalizeVariantIds, prodotti]);

  async function handleConfermaCoperti() {
    try {
      const selectedTables = Array.isArray(idTables) ? idTables.filter(Boolean) : [];
      const activeTable = tavolo || selectedTavolo || selectedTables[0];
      if (!activeTable?.id) {
        throw new Error(t("Nessun tavolo selezionato!"));
      }
      const tablesForCovers = selectedTables.some((tab) => Number(tab?.id) === Number(activeTable.id))
        ? selectedTables
        : [activeTable, ...selectedTables];

      const coverCount = getPositiveInt(numeroCoperti, 1);
      confirmedCoversRef.current = { tableId: Number(activeTable.id), count: coverCount };
      const tableCapacity = getPositiveInt(activeTable.maxSeats, activeTable.seats, activeTable.capacity);
      const tableGuestAllocations = buildTableGuestAllocations(activeTable, tablesForCovers, coverCount);
      const primaryAllocation = tableGuestAllocations.find((entry) => Number(entry.tableId) === Number(activeTable.id));
      let nTavolo = addTableFlag
        ? getPositiveInt(primaryAllocation?.guestCount, tableCapacity > 0 ? Math.min(coverCount, tableCapacity) : coverCount)
        : coverCount;
      if (addTableFlag) {
        for (const tab of tablesForCovers) {
          const allocation = tableGuestAllocations.find((entry) => Number(entry.tableId) === Number(tab.id));
          const seatsForTable = allocation?.guestCount ?? 0;
          await claimAndTrackTableSession(tab.id);
          await aggiornaTavolo(tab.id, { status: 'OCCUPATO', takenSeats: Math.max(0, seatsForTable) }, token, tenant);
        }
      } else {
        await claimAndTrackTableSession(activeTable.id);
        await aggiornaTavolo(activeTable.id, { status: 'OCCUPATO', takenSeats: nTavolo }, token, tenant);
      }

      onTableOccupied?.();
      tableWasOccupiedRef.current = true;

      const copertoProduct = getCopertoProductForTable(activeTable);
      if (copertoProduct) {
        const copertoQtyDraft = Math.max(
          1,
          coverCount,
          Number(nTavolo) || 0
        );
        const copertoKey = buildComboKey(copertoProduct.id, []);
        setQuantita((prev) => ({
          ...prev,
          [copertoKey]: copertoQtyDraft,
        }));
      }

      setOpenModalConfirmSeat(false);
    } catch (err) {
      handleAlert(err.message, err?.status === 409 ? 'warning' : 'error');
    }
  }

  function resetConfermaCoperti() {
    setNumeroCoperti(getDefaultSeatsForContext(activeReservation, tavolo || selectedTavolo));
    setConfirmReservationSeats(Boolean(activeReservation));
    setAddTableFlag(false);
  }
  function handleAlert(msg, type) { showSnackbar(msg, type); }

  function goToCheckoutForTable(tableId) {
    if (!tableId) return;
    if (isRestaurantTenant && !can('receipts:manage')) {
      navigate('/mappa_tavoli', { replace: true });
      return;
    }
    navigate(`/punto_cassa?tableId=${Number(tableId)}`, { state: { checkoutTableId: Number(tableId) } });
  }

  const getPackagePrice = (pkg) => resolvePackagePrice(pkg, prodotti);
  const getPackageImages = (pkg) => {
    if (!pkg || !Array.isArray(pkg.imagePaths)) return [];
    return pkg.imagePaths
      .map((path) => (typeof path === 'string' ? path.trim() : ''))
      .filter(Boolean);
  };

  const getPackageItemsLabel = (pkg) => {
    if (!pkg || !Array.isArray(pkg.items) || pkg.items.length === 0) return '-';
    return getOrderedPackageItems(pkg).map(item => {
      const product = item?.product || prodotti.find(p => String(p.id) === String(item.productId));
      const qty = Math.max(1, parseInt(item.quantity || 1, 10) || 1);
      const name = product?.name || t('newproduct');
      const variantName = getPackageItemVariantName(item);
      const course = getCourseData(item);
      const coursePrefix = isTastingMenu(pkg)
        ? `${course.index}${course.name ? ` ${course.name}` : ''}: `
        : '';
      return `${coursePrefix}${name}${variantName ? ` + ${variantName}` : ''} x${qty}`;
    }).join(', ');
  };

  const applyPackageDelta = (pkg, delta) => {
    if (!pkg || !delta) return;
    if (delta > 0) setCercaPiatto('');
    setPackageQuantita(prev => {
      const next = { ...prev };
      const current = next[pkg.id] || 0;
      next[pkg.id] = Math.max(current + delta, 0);
      return next;
    });
  };

  const shiftPackageImage = (packageId, totalImages, delta) => {
    if (!packageId || totalImages <= 1) return;
    setPackageImageIndex((prev) => {
      const current = Number(prev[packageId] || 0);
      const next = (current + delta + totalImages) % totalImages;
      return { ...prev, [packageId]: next };
    });
  };

  useEffect(() => {
    setPackageImageIndex({});
  }, [availablePackages]);

  const productSearchQuery = cercaPiatto.trim().toLowerCase();
  const matchesProductSearch = (item, extraText = '') => {
    if (!productSearchQuery) return true;

    const variantLabels = Array.isArray(item?.variants)
      ? item.variants.map((variant) => variant?.variant?.name || variant?.name)
      : [];

    return [
      item?.name,
      item?.description,
      item?.code,
      item?.sku,
      item?.barcode,
      item?.boardBasis,
      extraText,
      ...variantLabels,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(productSearchQuery);
  };

  const visiblePackages = productSearchQuery
    ? availablePackages.filter(pkg => matchesProductSearch(pkg, getPackageItemsLabel(pkg)))
    : selectedCategoria === 'ALL' && selectedMainCategory === 'ALL'
      ? availablePackages
      : [];

  const visibleProducts = prodotti.filter((product) =>
    matchesProductSearch(product) &&
    (productSearchQuery || selectedMainCategory === 'ALL' || productHasCategory(product, selectedMainCategory)) &&
    (productSearchQuery || selectedCategoria === 'ALL' || productHasSubcategory(product, selectedCategoria)) &&
    productMatchesActiveTableRoom(product)
  );

  const visibleCategoryIds = new Set(categorie.map((category) => Number(category.id)));
  const visibleSubcategories = sottoCategorie.filter((sub) => {
    const categoryId = Number(sub?.categoryId ?? sub?.category?.id);
    if (!visibleCategoryIds.has(categoryId)) return false;
    return selectedMainCategory === 'ALL' || categoryId === Number(selectedMainCategory);
  });

  const getPriceLabel = (product, price) => {
    if (isIncludedByBoardBasis(product, activeBoardBasis)) {
      return t("boardBasis.included", { defaultValue: "Included" });
    }
    if (!product.pricePerWeight) return `€${price.toFixed(2)}`;
    const unit = product.typeOfWeight === 'HECTOGRAM' ? 'hg' : 'kg';
    return `€${price.toFixed(2)}/${unit}`;
  };

  const handleVariantSelect = useCallback((selectedVariant) => {
    const selectedProductId = Number(
      prodottoSelezionato?.id ??
      selectedVariant?.productId ??
      selectedVariant?.product?.id
    );
    if (!selectedProductId) return;

    const normalizedVariant = {
      ...selectedVariant,
      productId: selectedProductId,
      product: selectedVariant?.product || prodottoSelezionato,
    };
    const selectedProduct = prodotti.find((product) => Number(product.id) === selectedProductId)
      || prodottoSelezionato
      || selectedVariant?.product;

    const currentProductVariants = getSelectedVariantsForProduct(selectedProductId);
    const oldVariantIds = currentProductVariants
      .map((variant) => Number(variant.id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .sort((a, b) => a - b);

    const exists = oldVariantIds.includes(Number(normalizedVariant.id));
    const nextProductVariants = exists
      ? currentProductVariants.filter((variant) => Number(variant.id) !== Number(normalizedVariant.id))
      : [...currentProductVariants, normalizedVariant];
    const nextVariantIds = nextProductVariants
      .map((variant) => Number(variant.id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .sort((a, b) => a - b);

    const oldComboKey = buildComboKey(selectedProductId, oldVariantIds);
    const nextComboKey = buildComboKey(selectedProductId, nextVariantIds);

    setVariantData((previousVariants) => {
      const variantsFromOtherProducts = previousVariants.filter(
        (variant) => Number(variant?.productId ?? variant?.product?.id) !== selectedProductId
      );
      return [...variantsFromOtherProducts, ...nextProductVariants];
    });

    setActiveVariantByProduct((prev) => ({
      ...prev,
      [selectedProductId]: nextProductVariants.length
        ? Number(nextProductVariants[nextProductVariants.length - 1].id)
        : null,
    }));

    if (selectedProduct?.pricePerWeight) {
      const weightRows = Array.isArray(quantitaPeso[selectedProductId])
        ? quantitaPeso[selectedProductId]
        : [];
      const targetIndex = weightRows.findIndex((quantity, index) => {
        if (Number(quantity || 0) <= 0) return false;
        return sameVariantIds(weightVariantRows[selectedProductId]?.[index], oldVariantIds);
      });
      const fallbackIndex = weightRows.findIndex((quantity) => Number(quantity || 0) > 0);
      const rowIndexToUpdate = targetIndex >= 0 ? targetIndex : fallbackIndex;

      if (rowIndexToUpdate >= 0) {
        setWeightVariantRows((prev) => ({
          ...prev,
          [selectedProductId]: {
            ...(prev[selectedProductId] || {}),
            [rowIndexToUpdate]: nextVariantIds,
          },
        }));
      }
      return;
    }

    if (oldComboKey !== nextComboKey) {
      setQuantita((prev) => {
        const oldQty = Number(prev[oldComboKey] || 0);
        if (oldQty <= 0) return prev;

        const next = { ...prev };
        if (oldQty > 1) {
          next[oldComboKey] = oldQty - 1;
        } else {
          delete next[oldComboKey];
        }
        next[nextComboKey] = Number(next[nextComboKey] || 0) + 1;
        return next;
      });
    }
  }, [
    buildComboKey,
    getSelectedVariantsForProduct,
    prodotti,
    prodottoSelezionato,
    quantitaPeso,
    sameVariantIds,
    weightVariantRows,
  ]);
  return (
    <Box sx={{
      backgroundColor: 'transparent',
      width: '100%',
      maxWidth: '100%',
      boxSizing: 'border-box',
      px: { xs: 1, md: 2.5 },
      pt: { xs: 0.75, md: 2 },
      pb: { xs: 1, md: 2 },
      display: 'flex',
      flexDirection: 'column',
      height: {
        xs: open ? '100%' : 'auto',
        lg: open ? 'min(88vh, 920px)' : 'calc(100dvh - 96px)',
      },
      minHeight: {
        xs: open ? 0 : 'calc(100dvh - 96px)',
        lg: 0,
      },
      maxHeight: { xs: open ? '100dvh' : 'none', lg: 'none' },
      overflowX: 'hidden',
      overflowY: open ? 'hidden' : { xs: 'visible', lg: 'hidden' },
      touchAction: 'pan-x pan-y pinch-zoom',
      overscrollBehaviorY: 'auto',
      WebkitOverflowScrolling: 'touch',
    }}>
      <Box sx={{
        width: '100%',
        maxWidth: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflowX: 'hidden',
        overflowY: open ? { xs: 'auto', lg: 'hidden' } : 'visible',
        WebkitOverflowScrolling: 'touch',
      }}>

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          position="sticky"
          top={0}
          zIndex={6}
          sx={{
            backgroundColor: open ? contextTP.background.paper : contextTP.background.default,
            py: { xs: 0.75, md: 0 },
            mb: { xs: 1, md: 0 },
          }}
        >
          <Typography variant="h5" mb={{ xs: 0, md: 3 }} mt={{ xs: 0, md: 3 }} sx={{ fontSize: { xs: 22, md: 24 }, lineHeight: 1.15 }}>
            {getTenantTranslation(t, tenantType, 'selectorders')}
          </Typography>
          {open && (
            <IconButton disableRipple onClick={handleClose}>
              <Close />
            </IconButton>
          )}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: { xs: 0.75, md: 1.5 }, flexWrap: 'wrap' }}>
          <Box
            marginBottom={{ xs: 0.5, md: 1.25 }}
            sx={{
              display: 'flex',
              flexWrap: { xs: 'nowrap', sm: 'wrap' },
              alignItems: 'center',
              gap: 0.65,
              width: { xs: '100%', md: 'auto' },
              maxWidth: '100%',
              overflowX: { xs: 'auto', sm: 'visible' },
              overflowY: 'hidden',
              pb: { xs: 0.5, sm: 0 },
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Button
              size="small"
              variant={selectedMainCategory === 'ALL' && selectedCategoria === 'ALL' ? 'contained' : 'outlined'}
              onClick={() => {
                setSelectedMainCategory('ALL');
                setSelectedCategoria('ALL');
              }}
              sx={{
                minHeight: 34,
                flex: { xs: '0 0 auto', sm: 'initial' },
                px: 1.5,
                backgroundColor: selectedMainCategory === 'ALL' && selectedCategoria === 'ALL'
                  ? contextTP.primary.main
                  : (contextTP.mode === 'light' ? '#fafafa' : '#333'),
                border: contextTP.mode === 'light' ? '#fafafa' : '#131313',
                color: selectedMainCategory === 'ALL' && selectedCategoria === 'ALL'
                  ? contextTP.primary.contrastText
                  : contextTP.text.primary,
                '&:hover': {
                  backgroundColor: selectedMainCategory === 'ALL' && selectedCategoria === 'ALL'
                    ? contextTP.primary.light
                    : contextTP.action.hover,
                  border: contextTP.mode === 'light' ? '#fafafa' : '#131313',
                },
                textTransform: 'none',
                fontWeight: 800,
              }}
            >
              {t('all')}
            </Button>

            {categorie.map(category => {
              const isSelected = Number(selectedMainCategory) === Number(category.id);
              return (
                <Button
                  key={`cat-${category.id}`}
                  size="small"
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => {
                    setSelectedMainCategory(category.id);
                    setSelectedCategoria('ALL');
                  }}
                  sx={{
                    minHeight: 34,
                    flex: { xs: '0 0 auto', sm: 'initial' },
                    px: 1.25,
                    backgroundColor: isSelected
                      ? contextTP.primary.main
                      : (contextTP.mode === 'light' ? '#fafafa' : '#333'),
                    border: contextTP.mode === 'light' ? '#fafafa' : '#131313',
                    color: isSelected ? contextTP.primary.contrastText : contextTP.text.primary,
                    '&:hover': {
                      backgroundColor: isSelected ? contextTP.primary.light : contextTP.action.hover,
                      border: contextTP.mode === 'light' ? '#fafafa' : '#131313',
                    },
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                >
                  {category.name}
                </Button>
              );
            })}

            {visibleSubcategories.length > 0 && (
              <Box sx={{
                width: '1px',
                alignSelf: 'stretch',
                minHeight: 26,
                display: { xs: 'none', sm: 'block' },
                backgroundColor: contextTP.mode === 'light' ? '#dddddd' : '#555',
                mx: 0.25,
              }} />
            )}

            {visibleSubcategories.map(sub => {
              const isSelected = Number(selectedCategoria) === Number(sub.id);
              return (
                <Button
                  key={`sub-${sub.id}`}
                  size="small"
                  variant={isSelected ? 'contained' : 'outlined'}
                  onClick={() => setSelectedCategoria(sub.id)}
                  sx={{
                    minHeight: 30,
                    flex: { xs: '0 0 auto', sm: 'initial' },
                    px: 1,
                    backgroundColor: isSelected
                      ? contextTP.primary.main
                      : (contextTP.mode === 'light' ? '#fff' : '#2c2c2c'),
                    border: contextTP.mode === 'light' ? '1px solid #dedede' : '1px solid #555',
                    color: isSelected ? contextTP.primary.contrastText : contextTP.text.primary,
                    '&:hover': {
                      backgroundColor: isSelected ? contextTP.primary.light : contextTP.action.hover,
                    },
                    textTransform: 'none',
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {sub.name}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' }, mb: { xs: 0.75, md: 1.25 }, width: { xs: '100%', md: 'auto' } }}>
            {String(tenantType || '').toUpperCase() === 'RISTORANTE' && categorie.length > 1 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SwapVertRoundedIcon />}
                onClick={openCategoryOrderDialog}
                sx={{
                  minHeight: 40,
                  minWidth: { xs: '100%', sm: 'auto' },
                  textTransform: 'none',
                  fontWeight: 800,
                }}
              >
                Ordina gruppi
              </Button>
            )}
            <TextField
              size="small"
              value={cercaPiatto}
              onChange={(event) => setCercaPiatto(event.target.value)}
              placeholder={t('checkout_page.searchProduct')}
              InputProps={{
                startAdornment: <SearchRoundedIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: cercaPiatto ? (
                  <IconButton
                    size="small"
                    aria-label={t('clear')}
                    onClick={() => setCercaPiatto('')}
                    edge="end"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                ) : null,
              }}
              sx={{
                minWidth: { xs: '100%', sm: 240 },
                width: { xs: '100%', sm: 280 },
                '& .MuiInputBase-root': {
                  backgroundColor: contextTP.mode === 'light' ? '#fafafa' : '#333333',
                  borderRadius: '8px',
                },
              }}
            />
            {tavolo && (
              <Box sx={{
                px: 2,
                py: 0.75,
                borderRadius: 2,
                background: contextTP.mode === 'light' ? '#FFF4E5' : 'rgba(253, 172, 78, 0.12)',
                border: `1px solid ${contextTP.mode === 'light' ? '#F0C58F' : 'rgba(253, 172, 78, 0.45)'}`,
                boxShadow: contextTP.mode === 'light'
                  ? '0 6px 18px rgba(212, 114, 0, 0.10)'
                  : '0 6px 18px rgba(0, 0, 0, 0.22)',
                minWidth: { xs: '100%', sm: 'auto' },
                textAlign: { xs: 'left', sm: 'center' },
              }}>
                <Typography sx={{
                  color: contextTP.text.primary,
                  fontSize: { xs: 18, md: 22 },
                  fontWeight: 800,
                  lineHeight: { xs: '24px', md: '28px' },
                }}>
                  {tavolo.name}{activeReservationDisplayLabel ? ` - ${activeReservationDisplayLabel}` : ''}
                </Typography>
                {tableNote && (
                  <Typography
                    variant="caption"
                    title={tableNote}
                    sx={{ display: 'block', mt: 0.25, color: contextTP.text.secondary, fontStyle: 'italic', maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {t('notes')}: {tableNote}
                  </Typography>
                )}
              </Box>
            )}

          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1.6fr) minmax(0, 0.8fr)', xl: 'minmax(0, 1.6fr) minmax(0, 0.8fr)' }, gap: { xs: 1, lg: 2 }, minHeight: 0, flex: 1, maxWidth: '100%', overflowX: 'hidden', overflowY: { xs: 'visible', lg: 'hidden' } }}>
          <Box sx={{ position: 'relative', minWidth: 0, maxWidth: '100%', minHeight: 0, overflowY: { xs: 'visible', lg: 'auto' }, overflowX: 'hidden', order: { xs: 2, lg: 1 } }}>
            <Grid container spacing={{ xs: 0.75, sm: 1.25 }} paddingBottom={{ xs: 7, sm: 10 }} sx={{ minHeight: { xs: 260, md: 420 }, width: '100%', maxWidth: '100%', m: 0 }}>

              {catalogLoading ? (
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                  <CircularProgress size={28} />
                </Grid>
              ) : (
                <>
                  {selectedMainCategory === 'ALL'
                    && selectedCategoria === 'ALL'
                    && activeFixedPriceMenus.map((menu) => {
                      const menuDraftCount = fixedMenuDrafts.filter(
                        (draft) => Number(draft.fixedPriceMenuId) === Number(menu.id)
                      ).length;
                      return (
                        <Grid key={`fixed-price-menu-${menu.id}`} item xs={12} sm={6} md={4} lg={2.4} xl={2.4} sx={{ display: 'flex', minWidth: 0 }}>
                          <Card sx={{
                            color: contextTP.mode === 'light' ? '#131313' : '#FAFAFA',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: showPic ? { xs: 'auto', md: 282, lg: 292 } : { xs: 'auto', md: 204, lg: 214 },
                            minHeight: { xs: 126, md: 0 },
                            overflow: { xs: 'visible', md: 'hidden' },
                            borderRadius: '8px',
                            border: '3px solid',
                            borderColor: menuDraftCount > 0 ? '#D47200' : 'transparent',
                            background: contextTP.mode === 'light' ? '#FAFAFA' : '#333333',
                          }}>
                            {showPic && (
                              <Box sx={{
                                height: { xs: 0, sm: 58, md: 76, lg: 96 },
                                display: { xs: 'none', sm: 'flex' },
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: contextTP.mode === 'light' ? '#FFF7ED' : '#2f2618',
                              }}>
                                <RestaurantMenuIcon sx={{ fontSize: 46, color: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E' }} />
                              </Box>
                            )}
                            <Box sx={{ p: { xs: 1, md: 1.5 }, display: 'flex', flexDirection: 'column', gap: { xs: 0.5, md: 1 }, flex: 1, minHeight: 0 }}>
                              <Typography variant="h6" fontWeight={900} sx={{ fontSize: { xs: 16, md: 20 }, lineHeight: 1.15 }}>{menu.name}</Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: { xs: 2, md: 3 }, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {(menu.groups || [])
                                  .map((group) => `${group.name}: ${group.minSelections === group.maxSelections ? group.minSelections : `${group.minSelections}-${group.maxSelections}`}`)
                                  .join(' | ')}
                              </Typography>
                              <Box sx={{ mt: 'auto', width: '100%' }}>
                                <Typography color="primary" fontWeight={900} fontSize={{ xs: 16, md: 18 }}>
                                  EUR {Number(menu.basePrice || 0).toFixed(2)}
                                </Typography>
                                <Stack direction="row" gap={1} alignItems="center" mt={0.5}>
                                  <Button fullWidth variant="contained" size="small" onClick={() => openFixedPriceMenuSelection(menu)}>
                                    {t("fixedPriceMenu.selectDishes")}
                                  </Button>
                                  {menuDraftCount > 0 && <Chip label={menuDraftCount} color="primary" size="small" />}
                                </Stack>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      );
                    })}

                  {/* Packages */}
                  {visiblePackages.map(pkg => {
                    const packageImages = getPackageImages(pkg);
                    const currentImageIndex = Number(packageImageIndex[pkg.id] || 0);
                    const imageUrl = packageImages.length ? packageImages[currentImageIndex % packageImages.length] : null;
                    const packageQty = Number(packageQuantita[pkg.id] || 0);
                    const isPackageSelected = packageQty > 0;
                    return (
                      <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={`package-${pkg.id}`} sx={{ display: 'flex', minWidth: 0 }}>
                        <Card sx={{
                          color: {
                            xs: isPackageSelected ? '#FFFFFF' : (contextTP.mode === 'light' ? '#131313' : '#FAFAFA'),
                            md: contextTP.mode === 'light' ? '#131313' : '#FAFAFA',
                          },
                          display: 'flex', padding: '1px', flexDirection: 'column',
                          alignItems: 'flex-start', borderRadius: '8px',
                          width: '100%',
                          height: showPic ? { xs: 'auto', md: 282, lg: 292 } : { xs: 'auto', md: 204, lg: 214 },
                          minHeight: { xs: 132, md: 0 },
                          overflow: { xs: 'visible', md: 'hidden' },
                          background: {
                            xs: isPackageSelected ? '#6A3900' : (contextTP.mode === 'light' ? '#FAFAFA' : '#333333'),
                            md: contextTP.mode === 'light' ? '#FAFAFA' : '#333333',
                          },
                          boxShadow: isPackageSelected
                            ? '0 6px 24.5px 0 rgba(212, 114, 0, 0.20)'
                            : '0 6px 24.5px 0 rgba(0, 0, 0, 0.16)',
                          border: { xs: '1px solid', md: '3px solid' },
                          borderColor: isPackageSelected ? '#FDAC4E' : 'transparent',
                        }}>
                          {showPic && (
                            <Box sx={{
                              display: { xs: 'none', sm: 'flex' }, width: '100%', height: { xs: 0, sm: 58, md: 76, lg: 96 },
                              aspectRatio: '41/24', alignItems: 'center', justifyContent: 'center',
                              borderBottom: `1px solid ${contextTP.mode === 'light' ? '#E0E0E0' : '#4F4F4F'}`,
                              background: contextTP.mode === 'light' ? '#FFF7ED' : '#2f2618',
                              overflow: 'hidden',
                              position: 'relative',
                            }}>
                              {imageUrl ? (
                                <>
                                  <img src={imageUrl} alt={pkg.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  {packageImages.length > 1 && (
                                    <>
                                      <IconButton
                                        size="small"
                                        onClick={() => shiftPackageImage(pkg.id, packageImages.length, -1)}
                                        sx={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.35)', color: '#fff' }}
                                      >
                                        <ChevronLeftRoundedIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() => shiftPackageImage(pkg.id, packageImages.length, 1)}
                                        sx={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.35)', color: '#fff' }}
                                      >
                                        <ChevronRightRoundedIcon fontSize="small" />
                                      </IconButton>
                                    </>
                                  )}
                                </>
                              ) : (
                                <Typography sx={{ fontWeight: 700, color: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E' }}>
                                  {isTastingMenu(pkg) ? t('orderPackages.tastingMenu') : t('orderPackages.label')}
                                </Typography>
                              )}
                            </Box>
                          )}

                          <IconButton disabled size="small" sx={{ visibility: 'hidden', display: { xs: 'none', sm: 'inline-flex' }, height: showPic ? 30 : 22, p: 0.5 }}>
                            <EditNoteOutlinedIcon />
                          </IconButton>

                          <Box sx={{
                            display: 'flex', padding: { xs: '9px', md: '12px' }, flexDirection: 'column',
                            alignItems: 'flex-start', gap: { xs: '5px', md: '7px' }, alignSelf: 'stretch',
                            flex: 1, minHeight: 0,
                            borderTop: `1px solid ${contextTP.mode === 'light' ? '#E0E0E0' : '#4F4F4F'}`,
                            background: {
                              xs: isPackageSelected ? '#6A3900' : (contextTP.mode === 'light' ? '#FAFAFA' : '#333333'),
                              md: isPackageSelected
                                ? contextTP.card?.product
                                : (contextTP.mode === 'light' ? '#FAFAFA' : '#333333'),
                            },
                          }}>
                            <Typography variant="h6" component="span" sx={{
                              fontSize: showPic ? { xs: '15px', md: '15px', lg: '19px' } : { xs: '15px', md: '14px', lg: '18px' },
                              fontWeight: 700,
                              lineHeight: { xs: '20px', md: '17px', lg: '22px' },
                              minHeight: showPic ? { xs: 32, md: 34, lg: 44 } : { xs: 30, md: 32, lg: 40 },
                              width: '100%',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}>
                              {pkg.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{
                              color: {
                                xs: isPackageSelected ? 'rgba(255,255,255,0.82)' : (contextTP.mode === 'light' ? '#303030' : '#B5B5B5'),
                                md: contextTP.mode === 'light' ? '#303030' : '#B5B5B5',
                              },
                              width: '100%', height: showPic ? { xs: '28px', md: '30px', lg: '36px' } : { xs: '28px', md: '28px', lg: '32px' }, fontSize: { xs: '10px', md: '10px', lg: '12px' }, fontWeight: 400, lineHeight: { xs: '14px', md: '15px', lg: '16px' },
                              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                            }}>
                              {isTastingMenu(pkg) ? t('orderPackages.tastingMenu') : t('orderPackages.label')} - {Array.isArray(pkg.items) ? pkg.items.length : '-'} {t('products')} - {getPackageItemsLabel(pkg)}
                            </Typography>

                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="stretch"
                              gap={0.5}
                              width="100%"
                              sx={{ mt: 'auto' }}
                            >
                              <Typography variant="body1" sx={{
                                color: {
                                  xs: isPackageSelected ? '#FDAC4E' : (contextTP.mode === 'light' ? '#CC4B00' : '#F9934B'),
                                  md: contextTP.mode === 'light' ? '#CC4B00' : '#F9934B',
                                },
                                fontSize: { xs: '17px', md: '15px', lg: '19px' }, fontWeight: 700, lineHeight: { xs: '22px', md: '18px', lg: '22px' },
                                whiteSpace: 'nowrap',
                                overflow: 'visible',
                              }}>
                                EUR {getPackagePrice(pkg).toFixed(2)}
                              </Typography>
                              <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.25} sx={{ width: '100%', minWidth: 0 }}>
                                <IconButton sx={{
                                  display: 'flex', width: { xs: '32px', md: '26px', lg: '32px' }, height: { xs: '32px', md: '26px', lg: '32px' },
                                  padding: 0,
                                  justifyContent: 'center', alignItems: 'center',
                                  flexShrink: 0, borderRadius: '8px',
                                  background: {
                                    xs: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                    md: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                  },
                                }} onClick={() => applyPackageDelta(pkg, 1)}>
                                  <AddRoundedIcon sx={{ fontSize: { xs: 20, md: 17, lg: 20 }, color: '#131313' }} />
                                </IconButton>
                                <Typography sx={{ minWidth: 18, flex: '0 1 auto', textAlign: 'center', fontSize: { xs: '16px', md: '13px', lg: '16px' }, fontWeight: 700, lineHeight: { xs: '20px', md: '16px', lg: '20px' } }}>
                                  {packageQty}
                                </Typography>
                                <IconButton disabled={packageQty === 0} sx={{
                                  display: 'flex', width: { xs: '32px', md: '26px', lg: '32px' }, height: { xs: '32px', md: '26px', lg: '32px' },
                                  padding: 0,
                                  justifyContent: 'center', alignItems: 'center',
                                  flexShrink: 0, borderRadius: '8px',
                                  background: {
                                    xs: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                    md: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                  },
                                }} onClick={() => applyPackageDelta(pkg, -1)}>
                                  <RemoveRoundedIcon sx={{ fontSize: { xs: 20, md: 17, lg: 20 }, color: '#131313' }} />
                                </IconButton>
                              </Box>
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    );
                  })}

                  {/* Products */}
                  {visibleProducts.map(piatto => {
                      const selectedVariants = getSelectedVariantsForProduct(piatto.id);
                      const selectedVariant = getActiveVariantForProduct(piatto.id);
                      const isVariantSelected = selectedVariants.length > 0;
                      const dataToShow = isVariantSelected ? selectedVariant : piatto;
                      const selectedVariantIds = getComboVariantIds(piatto.id);
                      const currentComboKey = buildComboKey(piatto.id, selectedVariantIds);
                      const currentQty = quantita[currentComboKey] || 0;
                      const totalQtyForProduct = getProductTotalQty(piatto.id);
                      const weightRows = Array.isArray(quantitaPeso[piatto.id]) ? quantitaPeso[piatto.id] : [];
                      const weightTotalForProduct = weightRows.reduce((sum, q) => sum + Number(q || 0), 0);
                      const variantsExtra = selectedVariants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
                      const effectiveUnitPrice = getDraftProductUnitPrice(piatto, variantsExtra);
                      const hasVariants = Array.isArray(piatto.variants) && piatto.variants.length > 0;
                      const isProductSelected = totalQtyForProduct > 0 || weightTotalForProduct > 0;

                      return (
                        <Grid item xs={12} sm={6} md={4} lg={2.4} xl={2.4} key={piatto.id} sx={{ display: 'flex', minWidth: 0 }}>
                          <Card sx={{
                            color: {
                              xs: isRestaurantTenant
                                ? (contextTP.mode === 'light' ? '#131313' : '#FAFAFA')
                                : (isProductSelected ? '#FFFFFF' : (contextTP.mode === 'light' ? '#131313' : '#FAFAFA')),
                              md: contextTP.mode === 'light' ? '#131313' : '#FAFAFA',
                            },
                            display: 'flex', padding: '1px', flexDirection: 'column',
                            alignItems: 'flex-start', borderRadius: '8px',
                            width: '100%',
                            height: piatto.pricePerWeight
                              ? (showPic ? { xs: 'auto', md: 306, lg: 316 } : { xs: 'auto', md: 240, lg: 250 })
                              : (showPic
                                ? { xs: 'auto', md: 282, lg: 292 }
                                : { xs: 'auto', md: 178, lg: 188 }),
                            minHeight: {
                              xs: isRestaurantTenant ? 64 : (piatto.pricePerWeight ? 176 : (hasVariants ? 138 : 118)),
                              md: 0,
                            },
                            overflow: { xs: 'visible', md: 'hidden' },
                            background: {
                              xs: isRestaurantTenant
                                ? (contextTP.mode === 'light' ? '#FAFAFA' : '#333333')
                                : (isProductSelected ? '#6A3900' : (contextTP.mode === 'light' ? '#FAFAFA' : '#333333')),
                              md: contextTP.mode === 'light' ? '#FAFAFA' : '#333333',
                            },
                            boxShadow: isProductSelected
                              ? '0 6px 24.5px 0 rgba(212, 114, 0, 0.20)'
                              : '0 6px 24.5px 0 rgba(0, 0, 0, 0.16)',
                            border: { xs: '1px solid', md: '3px solid' },
                            borderColor: isProductSelected
                              ? '#D47200' : 'transparent',
                          }}>

                            {!showPic ? null : (
                              <Box sx={{
                                display: { xs: 'none', sm: 'flex' }, width: '100%', height: { xs: 0, sm: 58, md: 76, lg: 96 },
                                alignItems: 'center', aspectRatio: '41/24',
                                overflow: 'hidden', justifyContent: 'center',
                              }}>
                                {dataToShow.photoPath ? (
                                  <img
                                    src={dataToShow.photoPath}
                                    alt=" "
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                ) : (
                                  <DefaultPlaceHolder
                                    style={{ width: '75%', height: '75%', color: '#131313' }}
                                    fill={'#131313'}
                                  />
                                )}
                              </Box>
                            )}

                            <Box fullwidth display={"flex"} justifyContent={"space-between"} alignItems="center" sx={{ width: "-webkit-fill-available;", minHeight: showPic ? { xs: 26, sm: 30 } : { xs: 24, sm: 24 }, px: { xs: 1, sm: 0.5 }, display: { xs: isRestaurantTenant ? 'none' : 'flex', sm: 'flex' } }}>
                              <IconButton
                                size="small"
                                disabled={totalQtyForProduct === 0 || !totalQtyForProduct}
                                color="primary"
                                onClick={() => { setOpenNote(true); setProdottoNote(piatto); }}
                                sx={{
                                  display: { xs: totalQtyForProduct > 0 ? 'inline-flex' : 'none', sm: 'inline-flex' },
                                  width: { xs: 26, sm: 'auto' },
                                  height: { xs: 24, sm: 'auto' },
                                  p: { xs: 0, sm: 0.5 },
                                }}
                              >
                                <EditNoteOutlinedIcon />
                              </IconButton>

                              {hasVariants && isVariantSelected ? (
                                <Button size="small" sx={{ alignSelf: 'center', ml: 'auto', minWidth: 0, minHeight: { xs: 22, sm: 28 }, px: 0.75, py: 0, fontSize: { xs: 10, sm: 11 }, fontWeight: 800 }} onClick={() => {
                                  setVariantData((array) => array.filter(
                                    (ogg) => Number(ogg?.productId ?? ogg?.product?.id) !== Number(piatto.id)
                                  ));
                                  setActiveVariantByProduct((prev) => ({ ...prev, [piatto.id]: null }));
                                }}>
                                  {t('cancel')}
                                </Button>
                              ) : hasVariants && (
                                <Button size="small" sx={{
                                  alignSelf: 'center',
                                  ml: 'auto',
                                  minWidth: 0,
                                  minHeight: { xs: 22, sm: 28 },
                                  px: 0.75,
                                  py: 0,
                                  fontSize: { xs: 10, sm: 11 },
                                  fontWeight: 800,
                                  color: {
                                    xs: isProductSelected ? '#FFFFFF' : (contextTP.mode === 'light' ? '#CC4B00' : '#FDAC4E'),
                                    sm: contextTP.mode === 'light' ? '#CC4B00' : '#FDAC4E',
                                  },
                                }} onClick={() => {
                                  setOpenVariant(true);
                                  setProdottoSelezionato(piatto);
                                }}>
                                  {t('variants')}
                                </Button>
                              )}
                            </Box>

                            <Box sx={{
                              display: 'flex',
                              padding: piatto.pricePerWeight
                                ? (showPic ? { xs: '8px', md: '10px' } : { xs: '8px', md: '9px' })
                                : (showPic ? { xs: '8px', md: '12px' } : { xs: '8px', md: '10px' }),
                              flexDirection: 'column',
                              alignItems: 'flex-start', gap: showPic ? { xs: '4px', md: '7px' } : '5px', alignSelf: 'stretch',
                              flex: 1, minHeight: 0,
                              borderTop: `1px solid ${contextTP.mode === 'light' ? '#E0E0E0' : '#4F4F4F'}`,
                              background: {
                                xs: isRestaurantTenant
                                  ? (contextTP.mode === 'light' ? '#FAFAFA' : '#333333')
                                  : (isProductSelected ? '#6A3900' : (contextTP.mode === 'light' ? '#FAFAFA' : '#333333')),
                                md: isProductSelected
                                  ? contextTP.card?.product
                                  : (contextTP.mode === 'light' ? '#FAFAFA' : '#333333'),
                              },
                            }}>
                              <Typography variant="h6" component="span" sx={{
                                fontSize: showPic ? { xs: '15px', md: '15px', lg: '19px' } : { xs: '16px', md: '14px', lg: '18px' },
                                fontStyle: 'normal',
                                fontWeight: 700,
                                lineHeight: showPic ? { xs: '20px', md: '17px', lg: '22px' } : { xs: '18px', md: '16px', lg: '20px' },
                                maxWidth: '100%',
                                minHeight: { xs: isRestaurantTenant ? 0 : (showPic ? 30 : 28), md: showPic ? 34 : 32, lg: showPic ? 44 : 40 },
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}>
                                {piatto.name}&nbsp;&nbsp;&nbsp;
                              </Typography>

                              <Typography variant="body2" color="textSecondary" sx={{
                                display: { xs: isRestaurantTenant ? 'none' : '-webkit-box', sm: '-webkit-box' },
                                color: {
                                  xs: isProductSelected ? 'rgba(255,255,255,0.82)' : (contextTP.mode === 'light' ? '#303030' : '#B5B5B5'),
                                  md: contextTP.mode === 'light' ? '#303030' : '#B5B5B5',
                                },
                                width: '100%',
                                minHeight: showPic || piatto.pricePerWeight ? 18 : 16,
                                fontSize: showPic ? { xs: '11px', md: '10px', lg: '12px' } : { xs: '10px', md: '9.5px', lg: '11px' },
                                fontWeight: 400,
                                lineHeight: showPic ? { xs: '18px', md: '15px', lg: '18px' } : { xs: '16px', md: '14px', lg: '16px' },
                                WebkitLineClamp: piatto.pricePerWeight ? 2 : 1,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}>
                                {getProductCategoryLabels(piatto)}
                              </Typography>

                              <Box
                                display="flex"
                                flexDirection={{ xs: isRestaurantTenant ? 'row' : 'column', sm: 'column' }}
                                alignItems="stretch"
                                justifyContent="flex-start"
                                width="100%"
                                gap={piatto.pricePerWeight ? (showPic ? 0.75 : 0.5) : 0.5}
                                sx={{ mt: 'auto' }}
                              >
                                <Typography variant="body1" sx={{
                                  display: { xs: isRestaurantTenant ? 'none' : 'block', sm: 'block' },
                                  color: {
                                    xs: isProductSelected ? '#FDAC4E' : (contextTP.mode === 'light' ? '#CC4B00' : '#F9934B'),
                                    md: contextTP.mode === 'light' ? '#CC4B00' : '#F9934B',
                                  },
                                  textAlign: 'left',
                                  fontSize: showPic ? { xs: '16px', md: '15px', lg: '19px' } : { xs: '15px', md: '14px', lg: '16px' },
                                  fontStyle: 'normal',
                                  fontWeight: 700,
                                  lineHeight: showPic ? { xs: '22px', md: '18px', lg: '22px' } : { xs: '18px', md: '16px', lg: '18px' },
                                  whiteSpace: 'nowrap',
                                  overflow: 'visible',
                                }}>
                                  {getPriceLabel(piatto, effectiveUnitPrice)}
                                </Typography>

                                <Box sx={{ display: { xs: isRestaurantTenant ? 'flex' : 'none', sm: 'none' }, alignItems: 'center', width: '100%', gap: 1 }}>
                                  <Typography sx={{
                                    color: contextTP.mode === 'light' ? '#CC4B00' : '#F9934B',
                                    fontSize: 15,
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                  }}>
                                    {getPriceLabel(piatto, effectiveUnitPrice)}
                                  </Typography>
                                  {isProductSelected && (
                                    <Typography sx={{ ml: 'auto', fontSize: 13, fontWeight: 800 }}>
                                      x{piatto.pricePerWeight ? weightTotalForProduct : totalQtyForProduct}
                                    </Typography>
                                  )}
                                  <IconButton
                                    aria-label={`Aggiungi ${piatto.name}`}
                                    onClick={() => selectProductOnce(piatto)}
                                    sx={{
                                      ml: isProductSelected ? 0 : 'auto',
                                      width: 34,
                                      height: 34,
                                      border: '2px solid',
                                      borderColor: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                      color: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                    }}
                                  >
                                    <AddRoundedIcon />
                                  </IconButton>
                                </Box>

                                {/* ── FIX Bug 1: weight products now have +/- buttons */}
                                {piatto.pricePerWeight ? (
                                  <Box
                                    display="flex"
                                    flexDirection="column"
                                    gap={0.5}
                                    width="100%"
                                    sx={{
                                      display: { xs: isRestaurantTenant ? 'none' : 'flex', sm: 'flex' },
                                      minHeight: weightRows.length === 0 ? (showPic ? 62 : 58) : 'auto',
                                      maxHeight: weightRows.length === 0 ? 'none' : (showPic ? { xs: 82, md: 88, lg: 78 } : { xs: 72, md: 78, lg: 70 }),
                                      overflowY: weightRows.length === 0 ? 'visible' : 'auto',
                                      overflowX: 'hidden',
                                      pr: 0.25,
                                    }}
                                  >
                                    {weightRows.length === 0 ? (
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          height: showPic ? { xs: 32, md: 28, lg: 32 } : { xs: 30, md: 28, lg: 30 },
                                          fontSize: { xs: 12, md: 10, lg: 12 },
                                          fontWeight: 800,
                                          lineHeight: '14px',
                                          borderColor: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                          color: contextTP.mode === 'light' ? '#CC4B00' : '#FDAC4E',
                                        }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          addWeightRow(piatto.id);
                                        }}
                                      >
                                        {t('add')} {piatto.typeOfWeight === 'HECTOGRAM' ? 'hg' : 'kg'}
                                      </Button>
                                    ) : null}
                                    {weightRows.map((weightValue, idx) => (
                                      <Box key={`${piatto.id}-w-${idx}`} display="grid" gridTemplateColumns={{ xs: '30px minmax(42px, 1fr) 30px 24px', md: '26px minmax(34px, 1fr) 26px 20px', lg: '30px minmax(42px, 1fr) 30px 24px' }} alignItems="center" gap={0.5}>
                                        <IconButton
                                          sx={{
                                            display: 'flex', width: { xs: 30, md: 26, lg: 30 }, height: { xs: 30, md: 26, lg: 30 },
                                            justifyContent: 'center', alignItems: 'center',
                                            borderRadius: '8px',
                                            background: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            incrementWeightRow(piatto.id, idx);
                                          }}
                                        >
                                          <AddRoundedIcon />
                                        </IconButton>
                                        <TextField
                                          type="number"
                                          value={weightValue || 0}
                                          onChange={(e) => handleQuantity(piatto.id, idx, e.target.value)}
                                          onClick={(e) => e.stopPropagation()}
                                          inputProps={{ step: 0.1, min: 0, style: { textAlign: 'center' } }}
                                          sx={{
                                            minWidth: 0,
                                            '& input': { fontSize: { xs: '14px', md: '12px', lg: '14px' }, fontWeight: 800, padding: '4px 2px' },
                                            '& .MuiOutlinedInput-root': { height: { xs: 30, md: 26, lg: 30 } },
                                          }}
                                        />
                                        <IconButton
                                          sx={{
                                            display: 'flex', width: { xs: 30, md: 26, lg: 30 }, height: { xs: 30, md: 26, lg: 30 },
                                            justifyContent: 'center', alignItems: 'center',
                                            borderRadius: '8px',
                                            background: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                          }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            decrementWeightRow(piatto.id, idx);
                                          }}
                                        >
                                          <RemoveRoundedIcon color={contextTP.mode === 'light' ? '#131313' : '#FBFBFB'} />
                                        </IconButton>
                                        <Button
                                          size="small"
                                          color="error"
                                          sx={{ minWidth: { xs: 24, md: 20, lg: 24 }, width: { xs: 24, md: 20, lg: 24 }, height: { xs: 30, md: 26, lg: 30 }, px: 0, fontSize: { xs: 12, md: 10, lg: 12 }, fontWeight: 800 }}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            removeWeightRow(piatto.id, idx);
                                          }}
                                        >
                                          x
                                        </Button>
                                      </Box>
                                    ))}
                                    <Button
                                      size="small"
                                      sx={{
                                        minHeight: showPic ? 24 : 22,
                                        py: 0,
                                        fontSize: { xs: 11, md: 10, lg: 11 },
                                        fontWeight: 800,
                                        color: contextTP.mode === 'light' ? '#CC4B00' : '#FDAC4E',
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addWeightRow(piatto.id);
                                      }}
                                    >
                                      + {t('add')}
                                    </Button>
                                  </Box>
                                ) : (
                                  <Box display={{ xs: isRestaurantTenant ? 'none' : 'flex', sm: 'flex' }} alignItems="center" justifyContent="space-between" gap={0.25} sx={{ width: '100%', minWidth: 0, flexShrink: 0, pt: { xs: 0.25, md: 0 } }}>
                                    <IconButton sx={{
                                      display: 'flex', width: { xs: '30px', md: '26px', lg: '32px' }, height: { xs: '30px', md: '26px', lg: '32px' },
                                      padding: 0, justifyContent: 'center', alignItems: 'center',
                                      gap: '10px', flexShrink: 0, borderRadius: '8px',
                                      background: {
                                        xs: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                        md: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                      },
                                    }} onClick={() => incrementQuantity(piatto.id, selectedVariantIds)}>
                                      <AddRoundedIcon sx={{ fontSize: { xs: 20, md: 17, lg: 20 }, color: '#131313' }} />
                                    </IconButton>
                                    <Typography sx={{ minWidth: 18, flex: '0 1 auto', textAlign: 'center', fontSize: { xs: '15px', md: '13px', lg: '16px' }, fontStyle: 'normal', fontWeight: 700, lineHeight: { xs: '20px', md: '16px', lg: '20px' } }}>
                                      {currentQty}
                                    </Typography>
                                    <IconButton sx={{
                                      display: 'flex', width: { xs: '30px', md: '26px', lg: '32px' }, height: { xs: '30px', md: '26px', lg: '32px' },
                                      padding: 0, justifyContent: 'center', alignItems: 'center',
                                      gap: '10px', flexShrink: 0, borderRadius: '8px',
                                      background: {
                                        xs: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                        md: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
                                      },
                                    }} onClick={() => decrementQuantity(piatto.id, selectedVariantIds)}>
                                      <RemoveRoundedIcon sx={{ fontSize: { xs: 20, md: 17, lg: 20 }, color: '#131313' }} />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                      );
                    })}
                </>
              )}

              {isSendingOrder && (
                <Box sx={{
                  position: 'absolute', inset: 0, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  backgroundColor: 'rgba(0,0,0,0.25)', borderRadius: 1, zIndex: 2,
                }}>
                  <CircularProgress size={34} />
                </Box>
              )}
            </Grid>
          </Box>

          {/* ── FIX Bug 4: pass selectedTavolo so DettaglioComande works from +New Order */}
          <Box sx={{
            minHeight: 0,
            height: { xs: 'auto', lg: '100%' },
            overflow: { xs: 'visible', lg: 'hidden' },
            display: {
              xs: isRestaurantTenant
                ? (showMobileOrder && (activeCourseOrders.length > 0 || selectedProdotti > 0) ? 'flex' : 'none')
                : (activeCourseOrders.length > 0 || selectedProdotti > 0 ? 'flex' : 'none'),
              sm: activeCourseOrders.length > 0 || selectedProdotti > 0 ? 'flex' : 'none',
              md: 'flex',
            },
            flexDirection: 'column',
            order: { xs: 1, lg: 2 },
            position: { xs: isRestaurantTenant ? 'fixed' : 'static', sm: 'static' },
            inset: { xs: isRestaurantTenant ? 0 : 'auto', sm: 'auto' },
            zIndex: { xs: isRestaurantTenant ? 1400 : 'auto', sm: 'auto' },
            p: { xs: isRestaurantTenant ? 1 : 0, sm: 0 },
            backgroundColor: { xs: isRestaurantTenant ? contextTP.background.paper : 'transparent', sm: 'transparent' },
          }}>
            <Button
              variant="outlined"
              onClick={() => setShowMobileOrder(false)}
              sx={{ display: { xs: isRestaurantTenant ? 'flex' : 'none', sm: 'none' }, mb: 1, flexShrink: 0, fontWeight: 900 }}
            >
              Chiudi ordine
            </Button>
            <DettaglioComande
              key={tavolo?.id ?? selectedTavolo?.id ?? 'no-table'}
              open={open}
              tavoloId={tavolo?.id ?? selectedTavolo?.id}
              tavoli={tavoli}
              categorie={categorie}
              sottoCategorie={sottoCategorie}
              liveDraftItems={liveDraftItems}
              onDeleteLiveDraftItem={handleDeleteLiveDraftItem}
              onIncrementLiveDraftItem={(item) => changeLiveDraftQuantity(item, 1)}
              onDecrementLiveDraftItem={(item) => changeLiveDraftQuantity(item, -1)}
              onEditLiveDraftNote={editLiveDraftNote}
              onEditLiveDraftVariants={editLiveDraftVariants}
              mobileExpanded={isRestaurantTenant && showMobileOrder}
              selectedTargetCourseId={targetCourseId}
              onSelectTargetCourse={(orderId) => {
                setTargetCourseId((current) => (
                  Number(current) === Number(orderId) ? 'NEW' : String(orderId)
                ));
              }}
              onPersistedOrderChanged={fetchData}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Bottom bar */}
      <Box sx={{
        position: open ? 'relative' : 'sticky', left: 0, right: 0, bottom: 0, zIndex: 5, mt: open ? 0.5 : 1,
        flexShrink: 0,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        backgroundColor: open ? contextTP.background.paper : contextTP.background.default,
        borderTop: `1px solid ${contextTP.mode === 'light' ? '#E0E0E0' : '#3f3f3f'}`,
        py: 0.75,
      }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr auto 1fr' },
            alignItems: 'center',
            gap: { xs: 0.75, sm: 2 },
            px: { xs: 1, md: 2 },
            py: { xs: 0.75, sm: 1 },
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            background: contextTP.secondary?.reverse,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <Typography sx={{
              display: 'flex', width: { xs: '32px', sm: '36px' }, height: { xs: '32px', sm: '36px' }, fontSize: { xs: '14px', sm: '16px' },
              flexDirection: 'column', lineHeight: '20px', justifyContent: 'center', alignItems: 'center',
              flexShrink: 0, borderRadius: '35.714px',
              background: contextTP.mode === 'light' ? '#D47200' : '#FDAC4E',
              color: contextTP.mode === 'light' ? '#FAFAFA' : '#131313',
            }}>
              {selectedProdotti}
            </Typography>
            <Box sx={{ ml: 1, minWidth: 0 }}>
              <Typography sx={{ color: contextTP.mode === 'light' ? '#131313' : '#FAFAFA', fontSize: { xs: '13px', sm: '15px' }, fontWeight: 700, lineHeight: { xs: '16px', sm: '20px' } }}>
                {t('selectedarticles')}
              </Typography>
              <Typography sx={{ color: contextTP.mode === 'light' ? '#131313' : '#FAFAFA', fontSize: { xs: '12px', sm: '13px' }, fontWeight: 400, lineHeight: { xs: '15px', sm: '18px' } }}>
                {t('total')} {totaleCosti.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {open && (
            <Box sx={{ textAlign: { xs: 'left', sm: 'center' }, minWidth: { xs: 0, sm: 190 }, order: { xs: 3, sm: 2 } }}>
              <Typography sx={{ fontSize: 11, lineHeight: '14px', color: contextTP.text.secondary }}>
                {t('miscUi.destination')}
              </Typography>
              <Typography sx={{
                fontSize: 14,
                lineHeight: '18px',
                fontWeight: 800,
                color: contextTP.text.primary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}>
                {selectedTargetCourseLabel}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, order: { xs: 2, sm: 3 } }}>
            <Box sx={{ display: { xs: 'grid', sm: 'flex' }, gridTemplateColumns: { xs: '1fr 1fr 1fr' }, gap: { xs: 0.5, sm: 1 }, width: { xs: '100%', sm: 'auto' }, flexWrap: 'wrap', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
              <Button
                variant="outlined"
                disabled={
                  isSendingOrder
                  || isPrinting
                  || (open ? (!tavolo && !selectedTavolo) : !selectedTavolo)
                  || (activeCourseOrders.length === 0 && liveDraftItems.length === 0)
                }
                onClick={handlePrintDifferential}
                sx={{
                  height: { xs: '36px', sm: '40px' },
                  minWidth: { xs: 0, sm: '140px' },
                  px: { xs: 0.5, sm: 2 },
                  fontSize: { xs: 11, sm: 13 },
                  fontWeight: 700,
                }}
              >
                STAMPA
              </Button>
              <Button
                variant="outlined"
                disabled={
                  isSendingOrder
                  || isPrinting
                  || (open ? (!tavolo && !selectedTavolo) : !selectedTavolo)
                  || activeCourseOrders.length === 0
                }
                onClick={handleReprintFullOrder}
                sx={{
                  height: { xs: '36px', sm: '40px' },
                  minWidth: { xs: 0, sm: '190px' },
                  px: { xs: 0.5, sm: 2 },
                  fontSize: { xs: 10, sm: 13 },
                  fontWeight: 700,
                }}
              >
                RISTAMPA COMANDA
              </Button>
              <Button
                variant="contained"
                disabled={
                  open
                    ? (tavolo
                      ? cart.length + selectedPackages.length + fixedMenuDrafts.length === 0
                      : (!selectedTavolo || cart.length + selectedPackages.length + fixedMenuDrafts.length === 0)
                    ) || isSendingOrder || isPrinting
                    : cart.length + selectedPackages.length + fixedMenuDrafts.length === 0 || !selectedTavolo
                }
                onClick={() => handleSendOrder(cart, selectedPackages, fixedMenuDrafts)}
                sx={{
                  backgroundColor: contextTP.mode === 'light' ? '#131313' : '#FFF',
                  color: contextTP.mode === 'light' ? '#FBFBFB' : '#131313',
                  display: 'flex', height: { xs: '36px', sm: '40px' }, minWidth: { xs: 0, sm: '160px' },
                  py: { xs: 0.5, sm: '10px' }, px: { xs: 0.5, sm: '19px' }, justifyContent: 'center', alignItems: 'center', gap: '10px',
                }}
              >
                <Typography sx={{ textAlign: 'center', fontSize: { xs: '12px', sm: '15px' }, fontWeight: 700 }}>
                  {open ? t('follow') : t('seeOrder')}
                </Typography>
              </Button>
            </Box>
          </Box>

          <Button
            variant="outlined"
            disabled={activeCourseOrders.length === 0 && selectedProdotti === 0}
            onClick={() => setShowMobileOrder(true)}
            sx={{
              display: { xs: isRestaurantTenant ? 'flex' : 'none', sm: 'none' },
              gridColumn: '1 / -1',
              order: 4,
              width: '100%',
              minHeight: 38,
              fontWeight: 900,
            }}
          >
            Visualizza ordine
          </Button>
        </Box>
      </Box>

      {/* ── Confirm seats dialog */}
      {shouldRequestCovers && (
        <Dialog
          open={openModalConfirmSeat}
          onClose={() => setShowSummary(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              width: { xs: 'calc(100vw - 16px)', sm: '100%' },
              maxWidth: { xs: 'calc(100vw - 16px)', sm: 600 },
              m: { xs: 1, sm: 4 },
              overflowX: 'hidden',
            },
          }}
        >
          <DialogTitle>{t('confirmCovers')} - {tableName}</DialogTitle>
          <DialogContent dividers>
            <Box px={2} py={1}>
              {activeReservation && (
                <Box pt={2} display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1" sx={{ mr: 3 }}>{t('confirmClientQuestion')}</Typography>
                  <RadioGroup
                    row
                    value={String(confirmReservationSeats)}
                    onChange={(e) => {
                      const isReservationClient = e.target.value === 'true';
                      const activeTable = tavolo || selectedTavolo;
                      setConfirmReservationSeats(isReservationClient);
                      setIsNonReservationClientSelection(!isReservationClient);
                      updateNonReservationTableMarker(tenant, activeTable?.id, activeReservation?.id, !isReservationClient);
                      if (isReservationClient) {
                        autoAppliedReservationIdRef.current = null;
                        const reservationGuests = Number(activeReservation?.guests);
                        if (Number.isInteger(reservationGuests) && reservationGuests > 0) {
                          setNumeroCoperti(reservationGuests);
                        }
                      } else {
                        removeAutoAddedReservationPackages();
                        const tableSeats = Number(activeTable?.maxSeats);
                        if (Number.isInteger(tableSeats) && tableSeats > 0) {
                          setNumeroCoperti(tableSeats);
                        }
                      }
                    }}
                  >
                    <FormControlLabel value={false} control={<Radio />} label={t('no')} />
                    <FormControlLabel value={true} control={<Radio />} label={t('yes')} />
                  </RadioGroup>
                </Box>
              )}
              <Box pb={1} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="body1">{t('confirmSeats')}</Typography>
                <Box minWidth="25%" alignContent="center">
                  <TextField
                    type="number"
                    value={numeroCoperti}
                    size="small"
                    onChange={(e) => setNumeroCoperti(getPositiveInt(e.target.value))}
                    inputProps={{ min: 1 }}
                    sx={{ width: '80px' }}
                  />
                </Box>
              </Box>
              {isOverCapacityCoverCount && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Coperti superiori alla capienza del tavolo: il numero inserito verra salvato comunque.
                </Alert>
              )}
              <Box pb={1} display="flex" justifyContent="flex-start" alignItems="center">
                <Typography variant="body1">{t('addTableQuestion')}</Typography>
                <Checkbox disableRipple checked={addTableFlag} onChange={() => setAddTableFlag(!addTableFlag)} />
              </Box>
              {addTableFlag && (
                <FormControl sx={{ width: 'min-content' }}>
                  <Autocomplete
                    multiple
                    options={tavoli}
                    value={idTables}
                    getOptionLabel={(option) => `${option?.name} ${option?.maxSeats} ${t('seats')}`}
                    getOptionDisabled={(option) => option?.id === tavolo?.id}
                    filterSelectedOptions
                    onChange={(e, newValue) => {
                      const nextTables = Array.isArray(newValue) ? newValue : [];
                      setIdTables(nextTables);
                      if (!tavolo) {
                        setSelectedTavolo(nextTables[0] || null);
                      }
                    }}
                    renderInput={(params) => <TextField {...params} label={t('booking.table')} />}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const tagProps = getTagProps({ index });
                        const isFixed = option.id === tavolo?.id;
                        return (
                          <Chip
                            {...tagProps}
                            label={`${option?.name} ${option?.maxSeats} ${t('seats')}`}
                            onDelete={isFixed ? undefined : tagProps.onDelete}
                          />
                        );
                      })
                    }
                  />
                </FormControl>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              variant="contained"
              onClick={() => {
                resetConfermaCoperti();
                setOpenModalConfirmSeat(false);
                handleClose();
              }}
            >
              {t('cancel')}
            </Button>
            <Button variant="contained" onClick={handleConfermaCoperti} sx={{ mx: 1 }}>
              {t('confirm')}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* ── Order summary dialog */}
      <Dialog
        open={showSummary}
        onClose={() => setShowSummary(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: 'calc(100vw - 16px)', sm: '100%' },
            maxWidth: { xs: 'calc(100vw - 16px)', sm: 600 },
            m: { xs: 1, sm: 4 },
            overflowX: 'hidden',
          },
        }}
      >
        <DialogTitle>{t('orderSummary')} - {tableName}</DialogTitle>
        <DialogContent dividers>
          {cart.map(item => {
            const prodotto = prodotti.find(p => p.id === item.productId);
            const variantIds = Array.isArray(item.variantIds) ? item.variantIds : [];
            const selectedVariants = variantIds
              .map((id) => {
                const fromSnapshot = Array.isArray(item.variantSnapshots)
                  ? item.variantSnapshots.find((v) => Number(v.id) === Number(id))
                  : null;
                const fromProduct = Array.isArray(prodotto?.variants)
                  ? prodotto.variants.find((v) => Number(v.id) === Number(id))
                  : null;
                return fromSnapshot || fromProduct || null;
              })
              .filter(Boolean);
            const variantsLabel = selectedVariants.map((v) => v?.name).filter(Boolean).join(' + ');
            const variantsExtra = selectedVariants.reduce((sum, v) => sum + Number(v?.price || 0), 0);
            const pricing = getDraftProductPricing(prodotto, variantsExtra, item.quantity);
            const unitPrice = pricing.displayUnitPrice;
            const isIncluded = unitPrice === 0 && isIncludedByBoardBasis(prodotto, activeBoardBasis);
            return (
              <Box key={`${item.productId}-${variantIds.join('-') || 'base'}-${item.comboKey || 'line'}`} display="flex" justifyContent="space-between" mb={1}>
                <Typography>{variantsLabel ? `${prodotto?.name} + ${variantsLabel}` : prodotto?.name} x {item.quantity}</Typography>
                <Typography>
                  {isIncluded
                    ? t("boardBasis.included", { defaultValue: "Included" })
                    : `€${pricing.lineTotal.toFixed(2)}`}
                </Typography>
              </Box>
            );
          })}
          {selectedPackages.map(pkgItem => {
            const pkg = availablePackages.find(p => String(p.id) === String(pkgItem.packageId));
            return (
              <Box key={`summary-package-${pkgItem.packageId}`} display="flex" justifyContent="space-between" mb={1}>
                <Typography>{pkg?.name || t('orderPackages.label')} {pkgItem.packageId} x {pkgItem.quantity}</Typography>
                <Typography>€{((pkgItem.unitPrice || 0) * pkgItem.quantity).toFixed(2)}</Typography>
              </Box>
            );
          })}
          {fixedMenuDrafts.map((draft) => (
            <Box key={draft.clientId} mb={1.5}>
              <Box display="flex" justifyContent="space-between">
                <Typography fontWeight={800}>{draft.menuName} x {draft.quantity}</Typography>
                <Typography>EUR {(Number(draft.finalPrice || 0) * Number(draft.quantity || 1)).toFixed(2)}</Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {(draft.selections || [])
                  .map((selection) => `${selection.groupName}: ${selection.productName}`)
                  .join(' | ')}
              </Typography>
            </Box>
          ))}
          <Box mt={2} display="flex" justifyContent="space-between">
            <Typography variant="h6">{t('total')}</Typography>
            <Typography variant="h6">€{totaleCosti.toFixed(2)}</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSummary(false)}>{t('cancel')}</Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: contextTP.mode === 'light' ? '#131313' : '#FFF',
              color: contextTP.mode === 'light' ? '#FBFBFB' : '#131313',
              display: 'flex', height: '48px', minWidth: '173px',
              py: '10px', px: '19px', justifyContent: 'center', alignItems: 'center', gap: '10px',
            }}
            onClick={() => {
              setIsSendingOrder(true);
              handleCart(cart, selectedPackages, fixedMenuDrafts, targetCourseId)
                .then(() => {
                  onOrderSent?.();
                  setShowSummary(false);
                  resetQuantity();
                  window.dispatchEvent(new CustomEvent('comanda-creata'));
                  const tableId = tavolo?.id || selectedTavolo?.id;
                  if (!open) goToCheckoutForTable(tableId);
                })
                .catch(err => handleAlert(t('reservations_details.error') + err.message, 'error'))
                .finally(() => setIsSendingOrder(false));
            }}
          >
            {t('sendorder')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCategoryOrder}
        onClose={() => !savingCategoryOrder && setOpenCategoryOrder(false)}
        maxWidth="xs"
        fullWidth
        fullScreen={false}
        PaperProps={{ sx: { m: { xs: 1.5, sm: 4 }, width: { xs: 'calc(100% - 24px)', sm: '100%' } } }}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>Ordina gruppi</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Scegli l'ordine delle categorie nella tua comanda. La modifica vale solo per il tuo utente.
          </Typography>
          <Stack spacing={1}>
            {categoryOrderDraft.map((category, index) => (
              <Box
                key={category.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
              >
                <Typography sx={{ flex: 1, fontWeight: 800 }}>{category.name}</Typography>
                <IconButton
                  aria-label={`Sposta ${category.name} in alto`}
                  disabled={index === 0 || savingCategoryOrder}
                  onClick={() => moveCategoryOrder(index, -1)}
                >
                  <ArrowUpwardRoundedIcon />
                </IconButton>
                <IconButton
                  aria-label={`Sposta ${category.name} in basso`}
                  disabled={index === categoryOrderDraft.length - 1 || savingCategoryOrder}
                  onClick={() => moveCategoryOrder(index, 1)}
                >
                  <ArrowDownwardRoundedIcon />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button disabled={savingCategoryOrder} onClick={() => setOpenCategoryOrder(false)}>
            Annulla
          </Button>
          <Button
            variant="contained"
            disabled={savingCategoryOrder}
            onClick={persistCategoryOrder}
          >
            {savingCategoryOrder ? <CircularProgress size={22} color="inherit" /> : 'Salva ordine'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openFixedMenu}
        onClose={() => setOpenFixedMenu(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: '100%' },
            maxWidth: { xs: '100vw', sm: 900 },
            height: { xs: '100dvh', sm: 'auto' },
            maxHeight: { xs: '100dvh', sm: '90dvh' },
            m: { xs: 0, sm: 4 },
            borderRadius: { xs: 0, sm: 1 },
            overflowX: 'hidden',
          },
        }}
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Box>
              <Typography variant="h6" fontWeight={900}>
                {activeFixedPriceMenu?.name || t("fixedPriceMenu.menuFallback")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("fixedPriceMenu.selectDishesDescription")}
              </Typography>
            </Box>
            <Chip label={`EUR ${Number(activeFixedPriceMenu?.basePrice || 0).toFixed(2)}`} color="primary" />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {(activeFixedPriceMenu?.groups || []).map((group) => {
              const selectedIds = fixedMenuSelections[group.id] || [];
              const max = Number(group.maxSelections || 1);
              const valid = selectedIds.length <= max;
              return (
                <Box key={group.id}>
                  <Stack direction="row" justifyContent="space-between" mb={1}>
                    <Box>
                      <Typography variant="h6" fontWeight={900}>
                        {group.courseIndex}. {group.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("fixedPriceMenu.chooseUpTo", {
                          count: max,
                          defaultValue: `Puoi scegliere fino a ${max} articoli (facoltativo)`,
                        })}
                      </Typography>
                    </Box>
                    <Chip size="small" color={valid ? 'success' : 'warning'} label={`${selectedIds.length}/${max}`} />
                  </Stack>

                  {(group.options || []).length === 0 ? (
                    <Alert severity="warning">{t("fixedPriceMenu.noDishesConfigured")}</Alert>
                  ) : (
                    <Grid container spacing={1}>
                      {(group.options || []).map((option) => {
                        const product = option.product
                          || prodotti.find((item) => Number(item.id) === Number(option.productId));
                        const productId = Number(option.productId);
                        const checked = selectedIds.includes(productId);
                        const disabled = !checked && selectedIds.length >= max;
                        return (
                          <Grid item xs={12} sm={6} key={option.id || `${group.id}-${productId}`}>
                            <Card
                              variant="outlined"
                              onClick={() => !disabled && toggleFixedMenuOption(group, productId)}
                              sx={{
                                p: 1,
                                cursor: disabled ? 'default' : 'pointer',
                                borderColor: checked ? 'primary.main' : 'divider',
                                opacity: disabled ? 0.55 : 1,
                              }}
                            >
                              <FormControlLabel
                                sx={{ width: '100%', m: 0 }}
                                control={
                                  <Checkbox
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => toggleFixedMenuOption(group, productId)}
                                    onClick={(event) => event.stopPropagation()}
                                  />
                                }
                                label={
                                  <Stack direction="row" justifyContent="space-between" width="100%" gap={1}>
                                    <Typography>{product?.name || t("fixedPriceMenu.productFallback", { id: productId })}</Typography>
                                    {Number(option.extraPrice || 0) > 0 && (
                                      <Typography color="primary" fontWeight={900}>
                                        +EUR {Number(option.extraPrice).toFixed(2)}
                                      </Typography>
                                    )}
                                  </Stack>
                                }
                              />
                            </Card>
                          </Grid>
                        );
                      })}
                    </Grid>
                  )}
                </Box>
              );
            })}

            <Card variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t("fixedPriceMenu.menuPrice")}</Typography>
                  <Typography>EUR {Number(activeFixedPriceMenu?.basePrice || 0).toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>{t("fixedPriceMenu.supplements")}</Typography>
                  <Typography>EUR {fixedMenuSupplements.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography fontWeight={900}>{t("total")}</Typography>
                  <Typography fontWeight={900} color="primary">EUR {fixedMenuFinalPrice.toFixed(2)}</Typography>
                </Stack>
              </Stack>
            </Card>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenFixedMenu(false)}>{t("fixedPriceMenu.cancel")}</Button>
          <Button variant="contained" onClick={addFixedPriceMenuDraft} disabled={Boolean(fixedMenuInvalidGroup)}>
            {t("fixedPriceMenu.addToOrder")}
          </Button>
        </DialogActions>
      </Dialog>

      {openVariant && (
        <ModalVariant
          product={prodottoSelezionato}
          open={openVariant}
          onClose={() => setOpenVariant(false)}
          selectedVariantIds={getSelectedVariantsForProduct(prodottoSelezionato?.id).map((v) => Number(v.id))}
          onSelect={handleVariantSelect}
        />
      )}

      {openNote && (
        <ModalNotes
          open={openNote}
          onClose={() => { setOpenNote(false); setProdottoNote(null); }}
          prodotto={prodottoNote}
          note={note[prodottoNote?.id]}
          onChange={(id, value) => setNote(prev => ({ ...prev, [id]: value }))}
          readOnly={false}
        />
      )}
    </Box>
  );
}

export default SelezioneComanda;

